import { getShareFraction } from '../services/itemShares';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmationResult, onAuthStateChanged, signInWithPhoneNumber, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  founderAdminAuth,
  formatPhoneAuthError,
  founderAdminFunctions,
  getSharedRecaptchaVerifier,
  normalizePhone,
  resetSharedRecaptchaVerifier,
} from '../services/firebase';
import Logo from './Logo';
import PhoneInput from './PhoneInput';
import FounderWebImpersonationPanel from './FounderWebImpersonationPanel';
import FboDisbursementAdminPanel from './FboDisbursementAdminPanel';
import MerchQrAdminPanel from './MerchQrAdminPanel';
import { persistFounderPreviewSession } from '../services/founderPreview';
import { WebImpersonationSession } from '../services/webImpersonation';

type RangePreset = '7d' | '30d' | '90d' | 'custom';
type SortDir = 'asc' | 'desc';
type UserSortBy = 'name' | 'createdAt';
type GroupSortBy = 'name' | 'createdAt' | 'nextSettlementAt';
type AttributionDimension = 'source' | 'campaign' | 'medium' | 'param';
type AttributionPanelDimension = 'source' | 'campaign' | 'medium' | 'content' | 'term' | 'id' | 'referrer' | 'path';

type ListItem = {
  id: string;
  sessionId: string;
  sessionTitle: string;
  status: string;
  amount: number;
  paidAmount?: number;
  remainingAmount?: number;
  totalAmount?: number;
  isPartialPayment?: boolean;
  eventAt: string | null;
  organizerName: string;
  organizerPhone: string;
  payerName: string;
  payerPhone: string;
  groupId?: string | null;
  groupName?: string | null;
  paymentFlow?: 'instant_payout' | 'force_settlement' | 'standard';
  instantPayoutDecision?: string | null;
};

type AccountItem = {
  id: string;
  label: string;
  last4: string | null;
  accountType: string;
  status: string | null;
  plaidItemId?: string | null;
  paymentMethodId?: string | null;
};

type PayoutAccountSummary = {
  externalAccountId: string | null;
  astraAccountId: string | null;
  astraStatus: string | null;
  astraEligibilityStatus: string | null;
  label: string;
  last4: string | null;
  isAttached: boolean;
};

type SettlementItem = {
  id: string;
  groupId: string;
  groupName: string;
  status: string;
  eventAt: string | null;
};

type OrganizedSplitParticipantDetail = {
  phone: string;
  name: string;
  role: 'organizer' | 'invitee';
  amount: number;
  status: string;
  itemCount: number;
};

type OrganizedSplitInviteDetail = {
  id: string;
  name: string;
  phone: string;
  inviteToken: string | null;
  amount: number;
  paidAmount?: number;
  remainingAmount?: number;
  isPartialPayment?: boolean;
  status: string;
  paymentMethod: string | null;
  eventAt: string | null;
  assignedItemIds: string[];
  claimedItemCount: number;
  claimedAmount: number;
};

type OrganizedSplitItemShareDetail = {
  phone: string;
  name: string;
  fraction: number;
  amount: number;
  status: string;
};

type OrganizedSplitItemDetail = {
  id: string;
  name: string;
  amount: number;
  isTip: boolean;
  shareCount: number;
  shares: OrganizedSplitItemShareDetail[];
};

type OrganizedSplitDetail = {
  sessionId: string;
  title: string;
  site: string;
  status: string;
  createdAt: string | null;
  completedAt: string | null;
  organizerName: string;
  organizerPhone: string;
  amount: number;
  currency: string;
  peopleCount: number | null;
  perPerson: number | null;
  checkin: string | null;
  checkout: string | null;
  guests: number | null;
  isReceipt: boolean;
  tax: number | null;
  tipAmount: number | null;
  isItemClaimingEnabled: boolean;
  collectionSummary: {
    totalInvites: number;
    paidOrSettledInvites: number;
    unpaidInvites: number;
    collectedAmount: number;
    outstandingAmount: number;
  };
  participants: OrganizedSplitParticipantDetail[];
  invites: OrganizedSplitInviteDetail[];
  items: OrganizedSplitItemDetail[];
};

type GroupExpenseItem = {
  id: string;
  groupId: string;
  groupName: string;
  title: string;
  amount: number;
  currency: string;
  payerPhone: string;
  payerName: string;
  source: string;
  status: string;
  cycleId: string | null;
  participantCount: number;
  occurredAt: string | null;
  createdAt: string | null;
};

type GroupExpenseParticipantDetail = {
  phone: string;
  name: string;
  shareAmount: number;
  isClaimable: boolean;
};

type GroupExpenseReceiptItemShareDetail = {
  phone: string;
  name: string;
  fraction: number;
};

type GroupExpenseReceiptItemDetail = {
  id: string;
  name: string;
  amount: number;
  shares: GroupExpenseReceiptItemShareDetail[];
};

type GroupExpenseDetail = {
  id: string;
  groupId: string;
  groupName: string;
  title: string;
  source: string;
  amount: number;
  currency: string;
  payerPhone: string;
  payerName: string;
  cycleId: string | null;
  status: string;
  notes: string | null;
  occurredAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdByPhone: string | null;
  createdByName: string | null;
  sourceSessionId: string | null;
  sourceSessionPath: string | null;
  participants: GroupExpenseParticipantDetail[];
  claimState: {
    mode: string;
    claimedBy: Array<{ phone: string; name: string }>;
    outstandingClaimablePhones: Array<{ phone: string; name: string }>;
  } | null;
  receipt: {
    imageUrl: string | null;
    tax: number | null;
    tip: number | null;
    fees: number | null;
    items: GroupExpenseReceiptItemDetail[];
  } | null;
};

type DashboardPoint = {
  date: string;
  sessionsCreated: number;
  sessionsCompleted: number;
  successfulPayments: number;
  paymentVolume: number;
  groupSettlementsCreated: number;
};

type IdentityVerificationStatus =
  | 'not_started'
  | 'pending'
  | 'document_required'
  | 'verified'
  | 'permanently_rejected';

type UserReport = {
  phone: string;
  name: string;
  hasApp: boolean;
  createdAt: string | null;
  createdAtSource: 'user' | 'profile' | 'observed';
  identityStatus?: IdentityVerificationStatus;
  identityStatusLabel?: string;
  cardCount?: number;
  bankCount?: number;
  isCleroPlus?: boolean;
  hasGmailConnected?: boolean;
  pendingConnect: number;
  pendingPlatform: number;
  openSplitsOrganized: number;
  settledSplitsOrganized: number;
  organizedSplitCount: number;
  paidSplitCount: number;
  paymentsReceivedCount: number;
  paymentsReceivedAmount: number;
  paymentsReceivedInstantPayoutCount: number;
  paymentsReceivedInstantPayoutAmount: number;
  paymentsReceivedForceSettlementCount: number;
  paymentsReceivedForceSettlementAmount: number;
  paymentsDoneCount: number;
  paymentsDoneAmount: number;
  paymentsDoneInstantPayoutCount: number;
  paymentsDoneInstantPayoutAmount: number;
  paymentsDoneForceSettlementCount: number;
  paymentsDoneForceSettlementAmount: number;
  recurringParentSplitCount: number;
  recurringAutoPayEnabledCount: number;
  recurringOptedOutCount: number;
  connectedCardCount: number;
  connectedCards: AccountItem[];
  hasPaymentAccount: boolean;
  paymentAccount: AccountItem | null;
  hasPayoutAccount: boolean;
  payoutAccount: PayoutAccountSummary | null;
  riskProfile: {
    achSuccessCount: number;
    achFailureCount: number;
    failedPaymentCount: number;
    settledVolume: number;
    averagePayment: number;
    manualReviewRequired: boolean;
    instantPayoutDisabledReason: string | null;
    instantDisabledUntil: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  };
  series: Array<{
    date: string;
    organizedSplits: number;
    settledSplits: number;
    paymentsReceived: number;
    paymentsDone: number;
  }>;
  organizedSplits: ListItem[];
  openSplitsOrganizedItems: ListItem[];
  settledSplitsOrganizedItems: ListItem[];
  paidSplits: ListItem[];
  paymentsReceived: ListItem[];
  paymentsDone: ListItem[];
};

type GroupReport = {
  id: string;
  name: string;
  createdAt: string | null;
  groupType: string;
  admin: {
    phone: string;
    name: string;
    status: string;
    isAdmin: boolean;
    addedAt: string | null;
    joinedAt: string | null;
    respondedAt: string | null;
    addedByPhone: string | null;
    hasCleroAccount: boolean | null;
    source: string | null;
  } | null;
  members: Array<{
    phone: string;
    name: string;
    status: string;
    isAdmin: boolean;
    addedAt: string | null;
    joinedAt: string | null;
    respondedAt: string | null;
    addedByPhone: string | null;
    hasCleroAccount: boolean | null;
    source: string | null;
  }>;
  activeMemberCount: number;
  pendingMemberCount: number;
  currentExpensesTotal: number;
  expenseCount: number;
  expenseVolume: number;
  expenses: GroupExpenseItem[];
  nextSettlementAt: string | null;
  nextSettlementLabel: string;
  settlementCount: number;
  openSettlementSplits: number;
  settledSettlementSplits: number;
  paymentsReceivedCount: number;
  paymentsReceivedAmount: number;
  paymentsReceivedInstantPayoutCount: number;
  paymentsReceivedInstantPayoutAmount: number;
  paymentsReceivedForceSettlementCount: number;
  paymentsReceivedForceSettlementAmount: number;
  paymentsDoneCount: number;
  paymentsDoneAmount: number;
  paymentsDoneInstantPayoutCount: number;
  paymentsDoneInstantPayoutAmount: number;
  paymentsDoneForceSettlementCount: number;
  paymentsDoneForceSettlementAmount: number;
  series: Array<{
    date: string;
    settlementSplitsCreated: number;
    settlementSplitsCompleted: number;
    paymentsReceived: number;
    paymentsDone: number;
  }>;
  settlementDocs: SettlementItem[];
  splits: ListItem[];
  openSettlementSplitsItems: ListItem[];
  settledSettlementSplitsItems: ListItem[];
  paymentsReceived: ListItem[];
  paymentsDone: ListItem[];
};

type DashboardReport = {
  generatedAt: string;
  range: {
    preset: RangePreset;
    startDate: string;
    endDate: string;
    label: string;
    includeOffline?: boolean;
  };
  summary: {
    totalOpenSplits: number;
    totalSettledSplits: number;
    appUsersCount: number;
    webUsersCount: number;
    totalTrackedUsers: number;
    totalPendingConnect: number;
    totalPendingPlatform: number;
    identityVerification: {
      verifiedCount: number;
      nonVerifiedCount: number;
      documentRequiredCount: number;
      pendingCount: number;
      rejectedCount: number;
      notStartedCount: number;
      nonVerifiedUsers: Array<{
        phone: string;
        name: string;
        status: IdentityVerificationStatus;
        statusLabel: string;
        hasApp: boolean;
      }>;
    };
    lifetimeTransactions: {
      totalGrossVolume: number;
      totalGrossCount: number;
      bankPayments: { count: number; volume: number };
      offlineSettled: { count: number; volume: number };
      grossCleroFeeRevenue: number;
      cleroPlusExemptionRate: number;
      cleroPlusExemptCount: number;
    };
    cleroPlus: {
      activeSubscribersCount: number;
      trialingCount: number;
      monthlyRevenue: number;
      quarterlyRevenue: number;
      yearlyRevenue: number;
    };
    gmailConnectedUsersCount: number;
    requestGeneration: {
      totalGrossTrackedVolume: number;
      totalGrossTrackedCount: number;
      creditCard: { count: number; volume: number };
      gmailReceipts: { count: number; volume: number };
      manual: { count: number; volume: number };
      aiPhotoReceipts: { count: number; volume: number };
    };
    accountStats: {
      averageCardsPerUser: number;
      zeroCardsPercentage: number;
      zeroBanksPercentage: number;
      totalCardAccounts: number;
      totalBankAccounts: number;
      usersWithCardsCount: number;
      usersWithBanksCount: number;
    };
    userConcentration: {
      isConcentrated: boolean;
      top10PercentShare: number;
      top20PercentShare: number;
      summaryLabel: string;
      topUsersCount: number;
      topUsersVolume: number;
    };
    platformDailyLimit: {
      limitCents: number;
      usedCents: number;
      remainingCents: number;
      usedPercentage: number;
      isCriticalWarning: boolean;
      resetAt: string | null;
    };
    totalPaymentsReceivedAmount: number;
    totalPaymentsDoneAmount: number;
    instantPayoutPaymentsCount: number;
    instantPayoutPaymentsAmount: number;
    forceSettlementPaymentsCount: number;
    forceSettlementPaymentsAmount: number;
    activePayingUsers: number;
    averagePaymentsPerUser: number;
    averagePaymentVolumePerUser: number;
    monthlyPaymentVolumeGrowthRate: number;
    current30dPaymentVolume: number;
    previous30dPaymentVolume: number;
    monthlyUserSignupGrowthRate: number;
    current30dUserSignups: number;
    previous30dUserSignups: number;
    totalRevenue: number;
    expectedRevenue: number;
    unsettledVolume: number;
    totalRevenueGrowthRate: number;
    current30dRevenue: number;
    previous30dRevenue: number;
    recurringParentSessions: number;
    recurringChildSessions: number;
    recurringAutoPayEnabledInvites: number;
    recurringOptedOutInvites: number;
  };
  timeseries: DashboardPoint[];
  openSplitsDetails: ListItem[];
  settledSplitsDetails: ListItem[];
  paymentsReceived: ListItem[];
  paymentsDone: ListItem[];
  organizedSplits: ListItem[];
  paidSplits: ListItem[];
  groupSettlementEvents: SettlementItem[];
  recurringParentSessionItems: ListItem[];
  recurringChildSessionItems: ListItem[];
  recurringAutoPayInviteItems: ListItem[];
  recurringOptOutInviteItems: ListItem[];
  expectedRevenueItems: ListItem[];
  unsettledVolumeItems: ListItem[];
  websiteVisits: {
    summary: {
      totalUniqueVisitors: number;
      totalVisits: number;
    };
    timeseries: Array<{
      date: string;
      visits: number;
    }>;
    paths: Array<{
      path: string;
      visits: number;
      uniqueVisitors: number;
    }>;
  };
  attributionCoverage: {
    waitlistTotal: number;
    waitlistWithAttribution: number;
    coverageRate: number;
  };
  unknownAttributionBreakdown: {
    noQueryParams: number;
    onlyInternalParams: number;
    landingNoTag: number;
    otherUnknown: number;
  };
  routeClassBreakdown: {
    rows: Array<{
      key: 'marketing' | 'product_flow';
      visits: number;
      uniqueVisitors: number;
      waitlistSignups: number;
    }>;
  };
  repeatVisitorMetrics: {
    repeatVisitorCount: number;
    repeatVisitorRate: number;
    eventsPerVisitorP50: number;
    eventsPerVisitorP90: number;
  };
  landingContextBreakdown: {
    rows: Array<{
      key: 'landing_tagged' | 'landing_untagged' | 'landing_waitlist_open';
      visits: number;
      uniqueVisitors: number;
      waitlistSignups: number;
    }>;
  };
  attributionInsights: {
    sources: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    mediums: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    campaigns: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    contents: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    terms: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    ids: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    referrers: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    landingPaths: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    queryParamKeys: Array<{ key: string; visits: number; trafficShare: number }>;
  };
  attributionInsightsV2?: {
    periodComparison: {
      visits: { current: number; previous: number; delta: number };
      waitlistSignups: { current: number; previous: number; delta: number };
      conversionRate: { current: number; previous: number; delta: number };
    };
    topMovers: {
      byVisits: Array<{ dimension: 'source' | 'campaign'; key: string; currentVisits: number; previousVisits: number; visitsDelta: number; currentConversionRate: number; previousConversionRate: number; conversionDelta: number }>;
      byConversion: Array<{ dimension: 'source' | 'campaign'; key: string; currentVisits: number; previousVisits: number; visitsDelta: number; currentConversionRate: number; previousConversionRate: number; conversionDelta: number }>;
    };
    qualitySignals: {
      unknownShare: number;
      internalParamOnlyShare: number;
      repeatVisitorRate: number;
      repeatVisitorBuckets: { singleEvent: number; repeat2To3: number; repeat4Plus: number };
      referrerMismatches: Array<{ source: string; referrerDomain: string; visits: number }>;
    };
    opportunityBuckets: {
      highVolumeLowConversion: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
      lowVolumeHighConversion: Array<{ key: string; label: string; visits: number; uniqueVisitors: number; waitlistSignups: number; conversionRate: number; trafficShare: number; conversionShare: number }>;
    };
    funnel: {
      visits: number;
      uniqueVisitors: number;
      waitlistSignups: number;
      visitToVisitorRate: number;
      visitorToWaitlistRate: number;
      visitToWaitlistRate: number;
    };
  };
  waitlistConversionInsights?: {
    summary: {
      modalOpens: number;
      submitClicks: number;
      successfulSignups: number;
      closeWithoutSubmit: number;
      openToSubmitRate: number;
      openToSignupRate: number;
      submitToSignupRate: number;
    };
    timeseries: Array<{ date: string; opens: number; submitClicks: number; signups: number }>;
    byVariant: Array<{ key: string; opens: number; submitClicks: number; signups: number; openToSignupRate: number; submitToSignupRate: number }>;
    byDevice: Array<{ key: string; opens: number; signups: number; openToSignupRate: number }>;
    byTrigger: Array<{ key: string; opens: number; signups: number; openToSignupRate: number }>;
  };
  attribution: {
    dimension: AttributionDimension;
    paramKey: string | null;
    selectedKeys: string[];
    summary: {
      totalUniqueVisitors: number;
      totalVisits: number;
      totalWaitlistSignups: number;
      overallConversionRate: number;
    };
    timeseries: Array<{
      date: string;
      visits: number;
      waitlistSignups: number;
      conversionRate: number;
    }>;
    groups: Array<{
      key: string;
      label: string;
      uniqueVisitors: number;
      visits: number;
      waitlistSignups: number;
      conversionRate: number;
      trafficShare: number;
      conversionShare: number;
      visitorToWaitlistRate: number;
    }>;
  };
  organizedSplitDetailsBySessionId: Record<string, OrganizedSplitDetail>;
  groupExpenseDetailsById: Record<string, GroupExpenseDetail>;
  users: UserReport[];
  groups: GroupReport[];
  meta: {
    scannedSessions: number;
    scannedInvites: number;
    scannedCanonicalInvites: number;
    scannedUsers: number;
    scannedProfiles: number;
    scannedGroups: number;
    scannedGroupExpenses: number;
    scannedSettlements: number;
    scannedPlaidAccounts: number;
    scannedPayoutAccounts: number;
    scannedWaitlist: number;
    scannedWaitlistFunnelEvents: number;
    scannedWebAttributionEvents: number;
  };
};

type RawOrganizedSplitInviteDetail = OrganizedSplitInviteDetail & {
  token?: string | null;
};

type RawOrganizedSplitDetail = Omit<OrganizedSplitDetail, 'invites'> & {
  invites: RawOrganizedSplitInviteDetail[];
};

type RawDashboardReport = Omit<DashboardReport, 'organizedSplitDetailsBySessionId'> & {
  organizedSplitDetailsBySessionId: Record<string, RawOrganizedSplitDetail>;
};

type DetailRow = {
  label: string;
  value: string;
};

type MetricDetailState = {
  title: string;
  subtitle?: string;
  source?: string;
  items?: ListItem[];
  itemKind?: 'default' | 'organized_split';
  accounts?: AccountItem[];
  settlements?: SettlementItem[];
  rows?: DetailRow[];
};

type DashboardQuery = {
  preset: RangePreset;
  startDate?: string;
  endDate?: string;
  sortUsersBy: UserSortBy;
  sortUsersDir: SortDir;
  sortGroupsBy: GroupSortBy;
  sortGroupsDir: SortDir;
  includeOffline?: boolean;
  attributionDimension: AttributionDimension;
  attributionKeys?: string[];
  attributionParamKey?: string;
};

type StartFounderPayerPreviewSessionResult = {
  sessionId: string;
  launchPath: string;
};

type AdminAccessResponse = {
  authorized: boolean;
};

type AdminDashboardPageProps = {
  onImpersonationStarted: (session: WebImpersonationSession) => void;
};

const RECAPTCHA_CONTAINER_ID = 'admin-dashboard-recaptcha';

const logAdminAuth = (message: string, details?: Record<string, unknown>) => {
  if (details) {
    console.log(`[AdminAuth] ${message}`, details);
    return;
  }
  console.log(`[AdminAuth] ${message}`);
};

const logAdminDashboardDebug = (message: string, details?: Record<string, unknown>) => {
  if (import.meta.env.PROD) return;
  if (details) {
    console.log(`[AdminDashboard][Debug] ${message}`, details);
    return;
  }
  console.log(`[AdminDashboard][Debug] ${message}`);
};

const normalizeOrganizedSplitInvite = (invite: RawOrganizedSplitInviteDetail): OrganizedSplitInviteDetail => ({
  ...invite,
  inviteToken: String(invite?.inviteToken || invite?.token || '').trim() || null,
});

const normalizeDashboardReport = (report: RawDashboardReport): DashboardReport => {
  const users = Array.isArray(report?.users)
    ? report.users.map((user) => ({
      ...user,
      hasApp: user?.hasApp === true,
    }))
    : [];
  const totalTrackedUsers = Number(report?.summary?.totalTrackedUsers || users.length || 0);
  const appUsersCount = Number(
    report?.summary?.appUsersCount
    || users.filter((user) => user.hasApp === true).length
    || 0
  );
  const webUsersCount = Number(
    report?.summary?.webUsersCount
    || Math.max(0, totalTrackedUsers - appUsersCount)
    || 0
  );

  return {
    ...report,
    users,
    summary: {
      ...report.summary,
      appUsersCount,
      webUsersCount,
      totalTrackedUsers,
    },
    organizedSplitDetailsBySessionId: Object.fromEntries(
      Object.entries(report?.organizedSplitDetailsBySessionId || {}).map(([sessionId, detail]) => [
        sessionId,
        {
          ...detail,
          invites: Array.isArray(detail?.invites) ? detail.invites.map(normalizeOrganizedSplitInvite) : [],
        },
      ])
    ),
    attribution: {
      dimension: report?.attribution?.dimension || 'source',
      paramKey: report?.attribution?.paramKey || null,
      selectedKeys: Array.isArray(report?.attribution?.selectedKeys) ? report.attribution.selectedKeys : [],
      summary: {
        totalUniqueVisitors: Number(report?.attribution?.summary?.totalUniqueVisitors || 0),
        totalVisits: Number(report?.attribution?.summary?.totalVisits || 0),
        totalWaitlistSignups: Number(report?.attribution?.summary?.totalWaitlistSignups || 0),
        overallConversionRate: Number(report?.attribution?.summary?.overallConversionRate || 0),
      },
      timeseries: Array.isArray(report?.attribution?.timeseries) ? report.attribution.timeseries : [],
      groups: Array.isArray(report?.attribution?.groups) ? report.attribution.groups : [],
    },
    websiteVisits: {
      summary: {
        totalUniqueVisitors: Number(report?.websiteVisits?.summary?.totalUniqueVisitors || 0),
        totalVisits: Number(report?.websiteVisits?.summary?.totalVisits || 0),
      },
      timeseries: Array.isArray(report?.websiteVisits?.timeseries) ? report.websiteVisits.timeseries : [],
      paths: Array.isArray(report?.websiteVisits?.paths) ? report.websiteVisits.paths : [],
    },
    attributionCoverage: {
      waitlistTotal: Number(report?.attributionCoverage?.waitlistTotal || 0),
      waitlistWithAttribution: Number(report?.attributionCoverage?.waitlistWithAttribution || 0),
      coverageRate: Number(report?.attributionCoverage?.coverageRate || 0),
    },
    unknownAttributionBreakdown: {
      noQueryParams: Number(report?.unknownAttributionBreakdown?.noQueryParams || 0),
      onlyInternalParams: Number(report?.unknownAttributionBreakdown?.onlyInternalParams || 0),
      landingNoTag: Number(report?.unknownAttributionBreakdown?.landingNoTag || 0),
      otherUnknown: Number(report?.unknownAttributionBreakdown?.otherUnknown || 0),
    },
    routeClassBreakdown: {
      rows: Array.isArray(report?.routeClassBreakdown?.rows) ? report.routeClassBreakdown.rows : [],
    },
    repeatVisitorMetrics: {
      repeatVisitorCount: Number(report?.repeatVisitorMetrics?.repeatVisitorCount || 0),
      repeatVisitorRate: Number(report?.repeatVisitorMetrics?.repeatVisitorRate || 0),
      eventsPerVisitorP50: Number(report?.repeatVisitorMetrics?.eventsPerVisitorP50 || 0),
      eventsPerVisitorP90: Number(report?.repeatVisitorMetrics?.eventsPerVisitorP90 || 0),
    },
    landingContextBreakdown: {
      rows: Array.isArray(report?.landingContextBreakdown?.rows) ? report.landingContextBreakdown.rows : [],
    },
    attributionInsights: {
      sources: Array.isArray(report?.attributionInsights?.sources) ? report.attributionInsights.sources : [],
      mediums: Array.isArray(report?.attributionInsights?.mediums) ? report.attributionInsights.mediums : [],
      campaigns: Array.isArray(report?.attributionInsights?.campaigns) ? report.attributionInsights.campaigns : [],
      contents: Array.isArray(report?.attributionInsights?.contents) ? report.attributionInsights.contents : [],
      terms: Array.isArray(report?.attributionInsights?.terms) ? report.attributionInsights.terms : [],
      ids: Array.isArray(report?.attributionInsights?.ids) ? report.attributionInsights.ids : [],
      referrers: Array.isArray(report?.attributionInsights?.referrers) ? report.attributionInsights.referrers : [],
      landingPaths: Array.isArray(report?.attributionInsights?.landingPaths) ? report.attributionInsights.landingPaths : [],
      queryParamKeys: Array.isArray(report?.attributionInsights?.queryParamKeys) ? report.attributionInsights.queryParamKeys : [],
    },
    attributionInsightsV2: report?.attributionInsightsV2
      ? report.attributionInsightsV2
      : {
        periodComparison: {
          visits: { current: 0, previous: 0, delta: 0 },
          waitlistSignups: { current: 0, previous: 0, delta: 0 },
          conversionRate: { current: 0, previous: 0, delta: 0 },
        },
        topMovers: { byVisits: [], byConversion: [] },
        qualitySignals: {
          unknownShare: 0,
          internalParamOnlyShare: 0,
          repeatVisitorRate: 0,
          repeatVisitorBuckets: { singleEvent: 0, repeat2To3: 0, repeat4Plus: 0 },
          referrerMismatches: [],
        },
        opportunityBuckets: { highVolumeLowConversion: [], lowVolumeHighConversion: [] },
        funnel: {
          visits: 0,
          uniqueVisitors: 0,
          waitlistSignups: 0,
          visitToVisitorRate: 0,
          visitorToWaitlistRate: 0,
          visitToWaitlistRate: 0,
        },
      },
    waitlistConversionInsights: report?.waitlistConversionInsights
      ? report.waitlistConversionInsights
      : {
        summary: {
          modalOpens: 0,
          submitClicks: 0,
          successfulSignups: 0,
          closeWithoutSubmit: 0,
          openToSubmitRate: 0,
          openToSignupRate: 0,
          submitToSignupRate: 0,
        },
        timeseries: [],
        byVariant: [],
        byDevice: [],
        byTrigger: [],
      },
  };
};

const hydrateUserChannels = async (report: DashboardReport): Promise<DashboardReport> => {
  const phones = Array.from(new Set(
    (report.users || [])
      .map((user) => normalizePhone(user.phone || ''))
      .filter(Boolean)
  ));

  if (phones.length === 0) {
    return {
      ...report,
      summary: {
        ...report.summary,
        appUsersCount: 0,
        webUsersCount: 0,
        totalTrackedUsers: 0,
      },
    };
  }

  try {
    const lookupCleroAccounts = httpsCallable<
      { phones: string[] },
      { results?: Array<{ phone?: string; hasCleroAccount?: boolean }> }
    >(founderAdminFunctions, 'lookupCleroAccounts');

    const chunks: string[][] = [];
    for (let index = 0; index < phones.length; index += 200) {
      chunks.push(phones.slice(index, index + 200));
    }

    const responses = await Promise.all(chunks.map((chunk) => lookupCleroAccounts({ phones: chunk })));
    const hasAppByPhone = new Map<string, boolean>();
    responses.forEach((response) => {
      const results = Array.isArray(response.data?.results) ? response.data.results : [];
      results.forEach((row) => {
        const phone = normalizePhone(String(row?.phone || ''));
        if (!phone) return;
        hasAppByPhone.set(phone, row?.hasCleroAccount === true);
      });
    });

    const users = report.users.map((user) => {
      const normalized = normalizePhone(user.phone || '');
      if (!normalized || !hasAppByPhone.has(normalized)) return user;
      return {
        ...user,
        hasApp: hasAppByPhone.get(normalized) === true,
      };
    });
    const appUsersCount = users.filter((user) => user.hasApp === true).length;
    const totalTrackedUsers = users.length;

    return {
      ...report,
      users,
      summary: {
        ...report.summary,
        appUsersCount,
        webUsersCount: Math.max(0, totalTrackedUsers - appUsersCount),
        totalTrackedUsers,
      },
    };
  } catch (error) {
    console.warn('[AdminDashboard] Failed to hydrate user channel statuses; using report payload as-is.', error);
    return {
      ...report,
      summary: {
        ...report.summary,
        totalTrackedUsers: Number(report.summary.totalTrackedUsers || report.users.length || 0),
      },
    };
  }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(Number(value || 0));

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: Math.abs(value) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const formatCount = (value: number) =>
  new Intl.NumberFormat('en-US').format(Math.max(0, Number(value || 0)));

const formatPercentDelta = (value: number) => {
  const normalized = Number(value || 0) * 100;
  const sign = normalized > 0 ? '+' : '';
  return `${sign}${normalized.toFixed(Math.abs(normalized) >= 10 ? 0 : 1)}%`;
};

const formatDate = (value: string | null, fallback = '—') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (value: string | null, fallback = '—') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const labelizeStatus = (value: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const labelizeGroupMemberStatus = (value: string) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'pending' ? 'Invited' : (normalized ? labelizeStatus(normalized) : 'Unknown');
};

const formatBooleanStatus = (value: boolean) => (value ? 'Yes' : 'No');

const formatAccountType = (value: string) => {
  const normalized = String(value || '').trim();
  return normalized ? labelizeStatus(normalized) : 'Unknown';
};

const formatPaymentFlow = (value?: string | null) => {
  if (value === 'instant_payout') return 'Instant payout';
  if (value === 'force_settlement') return 'Force settlement';
  if (value === 'standard') return 'Standard';
  return '—';
};

const formatExpenseSource = (value?: string | null) => {
  if (value === 'ai_receipt') return 'AI receipt';
  if (value === 'card') return 'Card';
  if (value === 'manual') return 'Manual';
  return value ? labelizeStatus(value) : 'Unknown';
};

const describeUserAccountCoverage = (user: UserReport) => {
  const statuses = [];
  if (user.hasPaymentAccount) statuses.push('Payment');
  if (user.hasPayoutAccount) statuses.push('Payout');
  return statuses.length ? statuses.join(' + ') : 'None';
};

const buildSeriesRows = (
  points: Array<{ date: string } & Record<string, number>>,
  pickValue: (point: { date: string } & Record<string, number>) => number,
  formatValue: (value: number) => string = formatCount
): DetailRow[] =>
  points
    .map((point) => ({ label: formatDate(point.date, point.date), value: formatValue(pickValue(point)) }))
    .filter((row) => row.value !== formatValue(0));

const buildPendingRows = (users: UserReport[], key: 'pendingConnect' | 'pendingPlatform'): DetailRow[] =>
  users
    .filter((user) => Number(user[key] || 0) > 0)
    .map((user) => ({
      label: `${user.name} (${user.phone})`,
      value: formatCurrency(user[key]),
    }));

const buildTopPayerRows = (users: UserReport[]): DetailRow[] =>
  users
    .filter((user) => user.paymentsDoneCount > 0)
    .sort((left, right) => right.paymentsDoneAmount - left.paymentsDoneAmount)
    .map((user) => ({
      label: `${user.name} (${user.phone})`,
      value: `${formatCount(user.paymentsDoneCount)} payments • ${formatCurrency(user.paymentsDoneAmount)}`,
    }));

const buildUserChannelRows = (users: UserReport[]): DetailRow[] =>
  [...users]
    .sort((left, right) => left.name.localeCompare(right.name, 'en-US', { sensitivity: 'base' }))
    .map((user) => ({
      label: `${user.name} (${user.phone})`,
      value: user.hasApp ? 'App' : 'Web',
    }));

const buildPaymentFlowRows = (params: {
  instantCount: number;
  instantAmount: number;
  forceCount: number;
  forceAmount: number;
}): DetailRow[] => [
  { label: 'Instant payout count', value: formatCount(params.instantCount) },
  { label: 'Instant payout volume', value: formatCurrency(params.instantAmount) },
  { label: 'Force settlement count', value: formatCount(params.forceCount) },
  { label: 'Force settlement volume', value: formatCurrency(params.forceAmount) },
];

const buildRiskProfileRows = (user: UserReport): DetailRow[] => [
  { label: 'ACH successes', value: formatCount(user.riskProfile.achSuccessCount) },
  { label: 'ACH failures', value: formatCount(user.riskProfile.achFailureCount) },
  { label: 'Failed payments', value: formatCount(user.riskProfile.failedPaymentCount) },
  { label: 'Settled volume', value: formatCurrency(user.riskProfile.settledVolume) },
  { label: 'Average payment', value: formatCurrency(user.riskProfile.averagePayment) },
  { label: 'Manual review required', value: formatBooleanStatus(user.riskProfile.manualReviewRequired) },
  { label: 'Instant payout disabled reason', value: user.riskProfile.instantPayoutDisabledReason || '—' },
  { label: 'Instant payout disabled until', value: formatDateTime(user.riskProfile.instantDisabledUntil) },
  { label: 'Risk profile created', value: formatDateTime(user.riskProfile.createdAt) },
  { label: 'Risk profile updated', value: formatDateTime(user.riskProfile.updatedAt) },
];

const buildAccountRows = (account: AccountItem | null): DetailRow[] =>
  account
    ? [
      { label: 'Label', value: account.label },
      { label: 'Last 4', value: account.last4 ? `•••• ${account.last4}` : '—' },
      { label: 'Type', value: formatAccountType(account.accountType) },
      { label: 'Status', value: account.status ? labelizeStatus(account.status) : '—' },
      { label: 'Payment method', value: account.paymentMethodId || '—' },
      { label: 'Plaid item', value: account.plaidItemId || '—' },
    ]
    : [];

const buildPayoutRows = (account: PayoutAccountSummary | null): DetailRow[] =>
  account
    ? [
      { label: 'Bank label', value: account.label },
      { label: 'Last 4', value: account.last4 ? `•••• ${account.last4}` : '—' },
      { label: 'Attached', value: formatBooleanStatus(account.isAttached) },
      { label: 'External account', value: account.externalAccountId || '—' },
      { label: 'Astra account', value: account.astraAccountId || '—' },
      { label: 'Astra status', value: account.astraStatus || '—' },
      { label: 'Astra eligibility', value: account.astraEligibilityStatus || '—' },
    ]
    : [];

const isRecaptchaishError = (err: any) => {
  const code = String(err?.code || '');
  const message = String(err?.message || '').toLowerCase();
  return (
    code === 'auth/captcha-check-failed'
    || code === 'auth/invalid-app-credential'
    || message.includes('captcha')
    || message.includes('app credential')
    || message.includes('malformed')
  );
};

const Sparkline = ({
  values,
  stroke = '#0f766e',
  fill = 'rgba(15, 118, 110, 0.12)',
  height = 72,
}: {
  values: number[];
  stroke?: string;
  fill?: string;
  height?: number;
}) => {
  const width = 180;
  const safeValues = values.length ? values : [0];
  const max = Math.max(...safeValues, 1);
  const points = safeValues.map((value, index) => {
    const x = safeValues.length === 1 ? width / 2 : (index / (safeValues.length - 1)) * width;
    const y = height - (value / max) * (height - 10) - 5;
    return `${x},${y}`;
  });
  const line = points.join(' ');
  const area = [`0,${height}`, ...points, `${width},${height}`].join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-20 w-full overflow-visible">
      <polygon points={area} fill={fill} />
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={line}
      />
    </svg>
  );
};

const BarTrend = ({
  values,
  color = '#164e63',
}: {
  values: number[];
  color?: string;
}) => {
  const width = 220;
  const height = 120;
  const safeValues = values.length ? values : [0];
  const max = Math.max(...safeValues, 1);
  const gap = 6;
  const barWidth = Math.max(6, Math.floor((width - gap * (safeValues.length - 1)) / safeValues.length));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-32 w-full">
      {safeValues.map((value, index) => {
        const scaledHeight = Math.max(4, (value / max) * (height - 8));
        const x = index * (barWidth + gap);
        const y = height - scaledHeight;
        return (
          <rect
            key={`${index}-${value}`}
            x={x}
            y={y}
            width={barWidth}
            height={scaledHeight}
            rx="4"
            fill={color}
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
};

const SummaryCard = ({
  label,
  value,
  note,
  source,
  onClick,
}: {
  label: string;
  value: string;
  note?: string;
  source?: string;
  onClick?: () => void;
}) => {
  const body = (
    <>
    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{label}</p>
    <p className="mt-3 text-3xl font-black tracking-tight text-slate-950">{value}</p>
    {note ? <p className="mt-2 text-sm text-slate-500">{note}</p> : null}
    {source ? <SourceNote source={source} className="mt-3" /> : null}
      {onClick ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">View details</p> : null}
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[28px] border border-slate-200 bg-white/90 p-5 text-left shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:border-slate-300"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
      {body}
    </div>
  );
};

const UserChannelCard = ({
  appUsers,
  webUsers,
  totalUsers,
  onClick,
}: {
  appUsers: number;
  webUsers: number;
  totalUsers: number;
  onClick?: () => void;
}) => {
  const body = (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">User Channels</p>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-emerald-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">App users</p>
          <p className="mt-2 text-2xl font-black tracking-tight text-emerald-950">{formatCount(appUsers)}</p>
        </div>
        <div className="rounded-2xl bg-amber-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">Web users</p>
          <p className="mt-2 text-2xl font-black tracking-tight text-amber-950">{formatCount(webUsers)}</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-slate-500">
        {formatCount(totalUsers)} tracked users. Live snapshot of <code>users/{'{phone}'}</code>; ignores range filter.
      </p>
      <SourceNote source="`users/{phone}` -> `hasApp`" className="mt-3" />
      {onClick ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">View details</p> : null}
    </>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[28px] border border-slate-200 bg-white/90 p-5 text-left shadow-[0_18px_50px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:border-slate-300"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-[28px] border border-slate-200 bg-white/90 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
      {body}
    </div>
  );
};

const ListSection = ({
  title,
  subtitle,
  items,
  source,
  itemKind = 'default',
  onOpenOrganizedSplit,
}: {
  title: string;
  subtitle: string;
  items: ListItem[];
  source?: string;
  itemKind?: 'default' | 'organized_split';
  onOpenOrganizedSplit?: (sessionId: string) => void;
}) => (
  <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-xl font-black text-slate-950">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        {source ? <SourceNote source={source} className="mt-3" /> : null}
      </div>
      <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
        {formatCount(items.length)}
      </div>
    </div>

    <div className="mt-5 space-y-3">
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
          No events in this range.
        </div>
      ) : (
        items.map((item) => (
          itemKind === 'organized_split' ? (
            <button
              key={item.id}
              type="button"
              onClick={() => onOpenOrganizedSplit?.(item.sessionId)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-left transition hover:border-slate-300 hover:bg-slate-100/80"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-950">{item.sessionTitle}</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Organized by {item.organizerName || item.organizerPhone}
                  </p>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                    Tap for full split detail
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {labelizeStatus(item.status)}
                  </span>
                  <span className="text-sm font-bold text-slate-900">{formatCurrency(item.amount)}</span>
                </div>
              </div>
            </button>
          ) : (
            <details key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              <summary className="cursor-pointer list-none">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-950">{item.sessionTitle}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      {item.payerName || '—'} to {item.organizerName || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.paymentFlow && item.paymentFlow !== 'standard' ? (
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                        {formatPaymentFlow(item.paymentFlow)}
                      </span>
                    ) : null}
                    {item.isPartialPayment || item.status === 'partially_paid' ? (
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
                        Partially Paid ({formatCurrency(item.paidAmount ?? item.amount)} / {formatCurrency(item.totalAmount ?? item.amount)})
                      </span>
                    ) : (
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {labelizeStatus(item.status)}
                      </span>
                    )}
                    <span className="text-sm font-bold text-slate-900">{formatCurrency(item.amount)}</span>
                  </div>
                </div>
              </summary>
              <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
                <div>
                  <p><span className="font-semibold text-slate-800">Session ID:</span> {item.sessionId}</p>
                  <p><span className="font-semibold text-slate-800">Event:</span> {formatDateTime(item.eventAt)}</p>
                  <p><span className="font-semibold text-slate-800">Flow:</span> {formatPaymentFlow(item.paymentFlow)}</p>
                  {item.isPartialPayment || item.status === 'partially_paid' ? (
                    <p><span className="font-semibold text-slate-800">Remaining:</span> {formatCurrency(item.remainingAmount || 0)}</p>
                  ) : null}
                </div>
                <div>
                  <p><span className="font-semibold text-slate-800">Organizer:</span> {item.organizerName || item.organizerPhone}</p>
                  <p><span className="font-semibold text-slate-800">Payer:</span> {item.payerName || item.payerPhone}</p>
                  <p><span className="font-semibold text-slate-800">Decision:</span> {item.instantPayoutDecision ? labelizeStatus(item.instantPayoutDecision) : '—'}</p>
                </div>
              </div>
            </details>
          )
        ))
      )}
    </div>
  </section>
);

const DetailMetric = ({
  label,
  value,
  source,
  onClick,
}: {
  label: string;
  value: string;
  source?: string;
  onClick?: () => void;
}) => {
  const body = (
    <>
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <p className="mt-2 text-lg font-black text-slate-950">{value}</p>
    {source ? <SourceNote source={source} className="mt-3" compact /> : null}
      {onClick ? <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">View details</p> : null}
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-2xl bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      {body}
    </div>
  );
};

const SourceNote = ({
  source,
  className = '',
  compact = false,
}: {
  source: string;
  className?: string;
  compact?: boolean;
}) => (
  <div className={`${className} rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-2`}>
    <p className={`font-medium text-slate-500 ${compact ? 'text-[10px] leading-4' : 'text-[11px] leading-5'}`}>
      <span className="font-semibold uppercase tracking-[0.16em] text-slate-400">Source</span>
      {' '}
      {source}
    </p>
  </div>
);

const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({ onImpersonationStarted }) => {
  const [authStatus, setAuthStatus] = useState<'checking' | 'signed_out' | 'awaiting_otp' | 'authorized' | 'unauthorized' | 'error'>('checking');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [authError, setAuthError] = useState('');
  const [accessMessage, setAccessMessage] = useState('');
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [founderPhone, setFounderPhone] = useState('');

  const [preset, setPreset] = useState<RangePreset>('7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortUsersBy, setSortUsersBy] = useState<UserSortBy>('name');
  const [sortUsersDir, setSortUsersDir] = useState<SortDir>('asc');
  const [sortGroupsBy, setSortGroupsBy] = useState<GroupSortBy>('name');
  const [sortGroupsDir, setSortGroupsDir] = useState<SortDir>('asc');
  const [includeOffline, setIncludeOffline] = useState(false);

  const [report, setReport] = useState<DashboardReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserReport | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupReport | null>(null);
  const [selectedMetricDetail, setSelectedMetricDetail] = useState<MetricDetailState | null>(null);
  const [selectedOrganizedSplitDetail, setSelectedOrganizedSplitDetail] = useState<OrganizedSplitDetail | null>(null);
  const [selectedGroupExpenseDetail, setSelectedGroupExpenseDetail] = useState<GroupExpenseDetail | null>(null);
  const [campaignPanelDimension, setCampaignPanelDimension] = useState<AttributionPanelDimension>('source');
  const [campaignPanelIncludeUnknown, setCampaignPanelIncludeUnknown] = useState(true);
  const [campaignRouteClassFilter, setCampaignRouteClassFilter] = useState<'all' | 'marketing' | 'product_flow'>('all');
  const [campaignLandingFilter, setCampaignLandingFilter] = useState<'all' | 'tagged' | 'untagged' | 'waitlist-open'>('all');
  const [openDeepDivePanel, setOpenDeepDivePanel] = useState<'campaign' | 'creative' | 'quality' | 'journeys' | null>('campaign');
  const [showDataDiagnostics, setShowDataDiagnostics] = useState(false);
  const [lifetimeTab, setLifetimeTab] = useState<'bank' | 'offline' | 'fee' | 'exemption'>('bank');
  const [requestsTab, setRequestsTab] = useState<'card' | 'gmail' | 'manual' | 'photo'>('card');
  const [cleroPlusPeriod, setCleroPlusPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [identityStatusFilter, setIdentityStatusFilter] = useState<'all' | 'document_required' | 'pending' | 'permanently_rejected' | 'not_started'>('all');
  const [identitySearchQuery, setIdentitySearchQuery] = useState('');
  const [showIdentityDetailModal, setShowIdentityDetailModal] = useState(false);
  const smsRequestInFlightRef = useRef(false);
  const hasSignedInUser = Boolean(founderAdminAuth.currentUser);

  const resetAdminRecaptchaVerifier = () => {
    resetSharedRecaptchaVerifier(RECAPTCHA_CONTAINER_ID);
  };

  const ensureAdminRecaptchaVerifier = async () => {
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (!container) {
      throw new Error('Verification UI not ready. Please refresh and try again.');
    }

    logAdminAuth('Creating fresh reCAPTCHA verifier.', { containerId: RECAPTCHA_CONTAINER_ID });
    const verifier = getSharedRecaptchaVerifier(RECAPTCHA_CONTAINER_ID, founderAdminAuth);
    if (!verifier) {
      throw new Error('Verification UI not ready. Please refresh and try again.');
    }
    return verifier;
  };

  const checkAdminAccess = async (candidatePhone?: string) => {
    const normalizedCandidate = candidatePhone ? normalizePhone(candidatePhone) : '';
    logAdminAuth('Checking admin access.', {
      candidatePhone: normalizedCandidate || '(current user)',
      hasAuthUser: Boolean(founderAdminAuth.currentUser),
    });
    const checkAccess = httpsCallable<{ phone?: string }, AdminAccessResponse>(
      founderAdminFunctions,
      'checkAdminDashboardAccess'
    );
    const result = await checkAccess(normalizedCandidate ? { phone: normalizedCandidate } : (founderPhone ? { phone: founderPhone } : {}));
    logAdminAuth('Admin access check completed.', {
      candidatePhone: normalizedCandidate || '(current user)',
      authorized: Boolean(result.data?.authorized),
    });
    return Boolean(result.data?.authorized);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(founderAdminAuth, async (user) => {
      const normalizedAuthPhone = normalizePhone(user?.phoneNumber || '');
      logAdminAuth('Auth state changed.', {
        signedIn: Boolean(user),
        phoneNumber: normalizedAuthPhone || null,
      });
      setAuthError('');
      setAccessMessage('');
      if (!user) {
        setAuthStatus('signed_out');
        setFounderPhone('');
        setReport(null);
        setSelectedUser(null);
        setSelectedGroup(null);
        setSelectedMetricDetail(null);
        setSelectedOrganizedSplitDetail(null);
        setSelectedGroupExpenseDetail(null);
        return;
      }

      setAuthStatus('checking');
      if (normalizedAuthPhone) {
        setFounderPhone(normalizedAuthPhone);
      }
      try {
        const authorized = await checkAdminAccess(normalizedAuthPhone);
        setAuthStatus(authorized ? 'authorized' : 'unauthorized');
        if (!authorized) {
          setReport(null);
          setSelectedUser(null);
          setSelectedGroup(null);
          setSelectedMetricDetail(null);
          setSelectedOrganizedSplitDetail(null);
          setSelectedGroupExpenseDetail(null);
        }
      } catch (error: any) {
        setAuthStatus('error');
        setReport(null);
        setLoadError(error?.message || 'Failed to verify admin access.');
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    return () => {
      resetAdminRecaptchaVerifier();
    };
  }, []);

  const query = useMemo<DashboardQuery>(() => ({
    preset,
    ...(preset === 'custom' && startDate ? { startDate } : {}),
    ...(preset === 'custom' && endDate ? { endDate } : {}),
    sortUsersBy,
    sortUsersDir,
    sortGroupsBy,
    sortGroupsDir,
    includeOffline,
    attributionDimension: 'source',
  }), [preset, startDate, endDate, sortUsersBy, sortUsersDir, sortGroupsBy, sortGroupsDir, includeOffline]);

  const openMetricDetail = (detail: MetricDetailState) => {
    setSelectedMetricDetail(detail);
  };

  const openOrganizedSplitDetail = (sessionId: string) => {
    const detail = report?.organizedSplitDetailsBySessionId?.[sessionId] || null;
    if (!detail) {
      console.warn('[AdminDashboard] Missing organized split detail for session.', { sessionId });
      return;
    }
    logAdminDashboardDebug('Opening organized split detail.', {
      sessionId,
      title: detail.title,
      invites: detail.invites.map((invite) => ({
        phone: invite.phone,
        name: invite.name,
        status: invite.status,
        inviteToken: invite.inviteToken,
      })),
    });
    setSelectedMetricDetail(null);
    setSelectedOrganizedSplitDetail(detail);
  };

  const openGroupExpenseDetail = (expenseId: string) => {
    const detail = report?.groupExpenseDetailsById?.[expenseId] || null;
    if (!detail) {
      console.warn('[AdminDashboard] Missing group expense detail.', { expenseId });
      return;
    }
    setSelectedMetricDetail(null);
    setSelectedGroupExpenseDetail(detail);
  };

  const normalizedPhone = normalizePhone(phone || '');
  const isValidAdminPhone = /^\+1\d{10}$/.test(normalizedPhone);

  const fetchReport = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const getReport = httpsCallable<DashboardQuery & { phone?: string }, RawDashboardReport>(
        founderAdminFunctions,
        'getAdminDashboardReport'
      );
      const result = await getReport(founderPhone ? { ...query, phone: founderPhone } : query);
      const normalizedReport = await hydrateUserChannels(normalizeDashboardReport(result.data));
      logAdminDashboardDebug('Admin report loaded.', {
        generatedAt: normalizedReport?.generatedAt || null,
        splitTokenSummary: Object.values(normalizedReport?.organizedSplitDetailsBySessionId || {}).map((detail) => ({
          sessionId: detail.sessionId,
          title: detail.title,
          inviteCount: detail.invites.length,
          invites: detail.invites.map((invite) => ({
            phone: invite.phone,
            status: invite.status,
            inviteToken: invite.inviteToken,
          })),
        })),
      });
      setReport(normalizedReport);
      setAuthStatus('authorized');
    } catch (error: any) {
      const code = String(error?.code || '');
      if (code.includes('permission-denied')) {
        setAuthStatus('unauthorized');
        setReport(null);
        setSelectedMetricDetail(null);
        setSelectedOrganizedSplitDetail(null);
        return;
      }
      const rawMessage = String(error?.message || '').trim();
      const isBareCode = rawMessage === 'INTERNAL' || rawMessage.startsWith('functions/') || /^[A-Z_]+$/.test(rawMessage);
      const friendlyMessage = isBareCode || !rawMessage
        ? 'Failed to load admin dashboard report. Please try again.'
        : rawMessage;
      setLoadError(friendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authStatus !== 'authorized' || !founderAdminAuth.currentUser) return;
    if (preset === 'custom' && (!startDate || !endDate)) return;
    void fetchReport();
  }, [authStatus, founderPhone, query, preset, startDate, endDate]);

  const handleSendCode = async (isRetry = false) => {
    if (smsRequestInFlightRef.current && !isRetry) {
      console.warn('[AdminAuth] Ignoring duplicate SMS request while one is already in progress.');
      return;
    }

    setAuthError('');
    setAccessMessage('');
    if (!isValidAdminPhone) {
      setAuthError('Please enter a valid phone number.');
      console.warn('[AdminAuth] Blocked OTP send for invalid phone.', { rawPhone: phone, normalizedPhone });
      return;
    }
    smsRequestInFlightRef.current = true;
    logAdminAuth('Starting admin OTP send flow.', { normalizedPhone, isRetry });
    setIsCheckingAccess(true);
    try {
      const authorized = await checkAdminAccess(normalizedPhone);
      if (!authorized) {
        console.warn('[AdminAuth] Phone failed admin access precheck.', { normalizedPhone });
        setAuthStatus('unauthorized');
        setAccessMessage('This phone number is not enabled for admin access.');
        setConfirmationResult(null);
        setOtp('');
        return;
      }
      setFounderPhone(normalizedPhone);
    } catch (error: any) {
      setAuthError(error?.message || 'Could not verify admin eligibility.');
      return;
    } finally {
      setIsCheckingAccess(false);
    }

    setIsSendingOtp(true);
    try {
      if (founderAdminAuth.currentUser) {
        logAdminAuth('Signing out existing auth user before OTP send.', {
          existingPhone: founderAdminAuth.currentUser.phoneNumber ? normalizePhone(founderAdminAuth.currentUser.phoneNumber) : null,
        });
        await signOut(founderAdminAuth);
      }
      const verifier = await ensureAdminRecaptchaVerifier();
      if (!verifier) throw new Error('Could not initialize verification. Please refresh and try again.');
      logAdminAuth('Calling signInWithPhoneNumber.', { normalizedPhone, isRetry });
      const result = await signInWithPhoneNumber(founderAdminAuth, normalizedPhone, verifier);
      logAdminAuth('OTP send succeeded.', { normalizedPhone });
      setConfirmationResult(result);
      setAuthStatus('awaiting_otp');
    } catch (error: any) {
      console.error('[AdminAuth] OTP send failed.', {
        code: error?.code || null,
        message: error?.message || String(error),
        normalizedPhone,
        isRetry,
      });
      if (!isRetry && isRecaptchaishError(error)) {
        console.warn('[AdminAuth] Retrying OTP send after reCAPTCHA/app credential failure.');
        resetAdminRecaptchaVerifier();
        await handleSendCode(true);
        return;
      }
      setAuthError(formatPhoneAuthError(error));
    } finally {
      setIsSendingOtp(false);
      smsRequestInFlightRef.current = false;
    }
  };

  const handleVerifyCode = async () => {
    if (!confirmationResult) return;
    setAuthError('');
    setIsVerifyingOtp(true);
    try {
      logAdminAuth('Verifying admin OTP.');
      await confirmationResult.confirm(otp);
      logAdminAuth('Admin OTP verification succeeded.');
      setOtp('');
      setConfirmationResult(null);
      setFounderPhone(normalizedPhone || founderPhone);
      resetAdminRecaptchaVerifier();
    } catch (error: any) {
      console.error('[AdminAuth] Admin OTP verification failed.', {
        code: error?.code || null,
        message: error?.message || String(error),
      });
      setAuthError(error?.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSignOut = async () => {
    logAdminAuth('Signing out admin user.');
    await signOut(founderAdminAuth);
    setConfirmationResult(null);
    setOtp('');
    setAuthStatus('signed_out');
    setAccessMessage('');
    setFounderPhone('');
    setSelectedMetricDetail(null);
    setSelectedOrganizedSplitDetail(null);
    resetAdminRecaptchaVerifier();
  };

  const handleLaunchPayerPreview = async (sessionId: string, invite: OrganizedSplitInviteDetail) => {
    logAdminDashboardDebug('Attempting payer preview launch.', {
      sessionId,
      phone: invite.phone,
      name: invite.name,
      status: invite.status,
      inviteToken: invite.inviteToken,
    });
    if (!invite.inviteToken) return;
    try {
      const startFounderPayerPreview = httpsCallable<
        { inviteToken: string; sessionId: string },
        StartFounderPayerPreviewSessionResult
      >(founderAdminFunctions, 'startFounderPayerPreviewSession');
      const result = await startFounderPayerPreview({
        inviteToken: invite.inviteToken,
        sessionId,
      });
      persistFounderPreviewSession({
        mode: 'payer_view',
        inviteToken: invite.inviteToken,
        sessionId,
        auditSessionId: result.data.sessionId,
        inviteName: invite.name || null,
        startedAt: new Date().toISOString(),
        returnPath: '/admin',
      });
      window.history.pushState({}, '', result.data.launchPath || `/pay/${encodeURIComponent(invite.inviteToken)}`);
      window.dispatchEvent(new Event('popstate'));
    } catch (error: any) {
      setLoadError(error?.message || 'Failed to launch founder payer session.');
    }
  };

  const activeDrawer = selectedUser || selectedGroup;
  const topSeries = report?.timeseries || [];
  const websiteSeries = report?.websiteVisits?.timeseries || [];
  const websitePaths = report?.websiteVisits?.paths || [];
  const attributionSeries = report?.attribution?.timeseries || [];
  const attributionGroups = report?.attribution?.groups || [];
  const routeClassRows = report?.routeClassBreakdown?.rows || [];
  const landingContextRows = report?.landingContextBreakdown?.rows || [];
  const attributionInsights = report?.attributionInsights;
  const topSources = attributionInsights?.sources?.slice(0, 8) || [];
  const topCampaigns = attributionInsights?.campaigns?.slice(0, 8) || [];
  const topContents = attributionInsights?.contents?.slice(0, 8) || [];
  const topTerms = attributionInsights?.terms?.slice(0, 8) || [];
  const topIds = attributionInsights?.ids?.slice(0, 8) || [];
  const topReferrers = attributionInsights?.referrers?.slice(0, 8) || [];
  const topParamKeys = attributionInsights?.queryParamKeys?.slice(0, 8) || [];
  const topWebsitePath = websitePaths[0] || null;
  const topAttributionGroup = attributionGroups[0] || null;
  const unknownAttributionGroup = attributionGroups.find((row) => row.key === 'unknown') || null;
  const attributionV2 = report?.attributionInsightsV2;
  const waitlistInsights = report?.waitlistConversionInsights;
  const panelDimensionRows = (() => {
    if (!report) return [];
    const map: Record<AttributionPanelDimension, typeof report.attribution.groups> = {
      source: report.attributionInsights.sources,
      campaign: report.attributionInsights.campaigns,
      medium: report.attributionInsights.mediums,
      content: report.attributionInsights.contents,
      term: report.attributionInsights.terms,
      id: report.attributionInsights.ids,
      referrer: report.attributionInsights.referrers,
      path: report.attributionInsights.landingPaths,
    };
    return map[campaignPanelDimension] || [];
  })();
  const campaignPanelRows = panelDimensionRows.filter((row) => campaignPanelIncludeUnknown || row.key !== 'unknown');
  const chartRows = campaignPanelRows.slice(0, 6);
  const trafficPieTotal = chartRows.reduce((sum, row) => sum + row.visits, 0);
  const funnelVisits = chartRows.reduce((sum, row) => sum + row.visits, 0);
  const funnelVisitors = chartRows.reduce((sum, row) => sum + row.uniqueVisitors, 0);
  const funnelWaitlist = chartRows.reduce((sum, row) => sum + row.waitlistSignups, 0);
  const routeClassVisibleRows = routeClassRows.filter((row) => campaignRouteClassFilter === 'all' || row.key === campaignRouteClassFilter);
  const landingVisibleRows = landingContextRows.filter((row) => (
    campaignLandingFilter === 'all'
    || (campaignLandingFilter === 'tagged' && row.key === 'landing_tagged')
    || (campaignLandingFilter === 'untagged' && row.key === 'landing_untagged')
    || (campaignLandingFilter === 'waitlist-open' && row.key === 'landing_waitlist_open')
  ));

  const shellClassName = 'min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(226,232,240,0.9),_rgba(248,250,252,0.96)_38%,_rgba(255,255,255,1)_100%)]';

  if (authStatus === 'checking') {
    return (
      <div className={`${shellClassName} flex items-center justify-center px-6`}>
        <div className="rounded-[32px] border border-slate-200 bg-white px-8 py-10 text-center shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">Admin Dashboard</p>
          <p className="mt-4 text-xl font-black text-slate-950">Checking access…</p>
        </div>
      </div>
    );
  }

  if (authStatus === 'signed_out' || authStatus === 'awaiting_otp') {
    return (
      <div className={`${shellClassName} flex min-h-screen items-center justify-center px-6 py-12`}>
        <div className="w-full max-w-xl rounded-[36px] border border-slate-200 bg-white/95 p-8 shadow-[0_28px_80px_rgba(15,23,42,0.12)]">
          <div className="flex items-center gap-4">
            <div className="rounded-[24px] bg-slate-100 p-3">
              <Logo size="xs" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500">Clero Internal</p>
              <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-950">Internal access</h1>
            </div>
          </div>

          <p className="mt-6 text-sm leading-7 text-slate-600">
            Enter your phone number. We’ll only send an OTP if that user already has admin access enabled.
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-slate-700">Phone number</span>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                placeholder="(555) 000-0000"
                className="[&_input]:border-slate-200 [&_input]:bg-slate-50 [&_input]:focus:border-slate-400 [&_input]:focus:bg-white [&_select]:border-slate-200 [&_select]:bg-slate-50 [&_select]:focus:border-slate-400 [&_select]:focus:bg-white"
              />
            </label>

            {authStatus === 'awaiting_otp' && confirmationResult ? (
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Verification code</span>
                <input
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  placeholder="123456"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </label>
            ) : null}
          </div>

          {authError ? (
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {authError}
            </div>
          ) : null}
          {accessMessage ? (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              {accessMessage}
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {authStatus === 'awaiting_otp' && confirmationResult ? (
              <>
                <button
                  onClick={handleVerifyCode}
                  disabled={!otp || isVerifyingOtp}
                  className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {isVerifyingOtp ? 'Verifying…' : 'Verify and continue'}
                </button>
                <button
                  onClick={() => {
                    setConfirmationResult(null);
                    setOtp('');
                    setAuthStatus('signed_out');
                    setAccessMessage('');
                    resetAdminRecaptchaVerifier();
                  }}
                  className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Use a different number
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  void handleSendCode();
                }}
                disabled={!isValidAdminPhone || isCheckingAccess || isSendingOtp}
                className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isCheckingAccess ? 'Checking access…' : isSendingOtp ? 'Sending code…' : 'Continue'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (authStatus === 'error') {
    return (
      <div className={`${shellClassName} flex min-h-screen items-center justify-center px-6`}>
        <div className="w-full max-w-xl rounded-[36px] border border-rose-200 bg-white p-8 shadow-[0_18px_60px_rgba(15,23,42,0.1)]">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-rose-600">Verification Error</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">Unable to verify admin access.</h1>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            {loadError || 'A network error or service hiccup occurred while checking admin access.'}
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => {
                setAuthStatus('checking');
                setLoadError('');
                const user = founderAdminAuth.currentUser;
                const normalized = normalizeAdminPhoneNumber(user?.phoneNumber || founderPhone);
                if (user && normalized) {
                  void checkAdminAccess(normalized)
                    .then((authorized) => {
                      setAuthStatus(authorized ? 'authorized' : 'unauthorized');
                    })
                    .catch((err) => {
                      setAuthStatus('error');
                      setLoadError(err?.message || 'Failed to verify admin access.');
                    });
                } else {
                  setAuthStatus('signed_out');
                }
              }}
              className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Try again
            </button>
            {hasSignedInUser ? (
              <button
                onClick={() => void handleSignOut()}
                className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Sign out
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (authStatus === 'unauthorized') {
    return (
      <div className={`${shellClassName} flex min-h-screen items-center justify-center px-6`}>
        <div className="w-full max-w-xl rounded-[36px] border border-amber-200 bg-white p-8 shadow-[0_18px_60px_rgba(15,23,42,0.1)]">
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-amber-600">Not authorized</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-950">This phone number can’t access the admin dashboard.</h1>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            This account is not enabled for admin access.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            {hasSignedInUser ? (
              <button
                onClick={() => void handleSignOut()}
                className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Sign out
              </button>
            ) : null}
            <button
              onClick={() => {
                setAuthStatus('signed_out');
                setConfirmationResult(null);
                setOtp('');
                setAuthError('');
                setAccessMessage('');
                void signOut(founderAdminAuth);
              }}
              className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Try another number
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <div className="mx-auto max-w-[1600px] px-4 pb-14 pt-6 sm:px-6 lg:px-8">
        <div className="mb-6 rounded-[32px] border border-slate-200 bg-white/85 px-5 py-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.34em] text-slate-500">Clero Operations</p>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">Admin dashboard</h1>
              <p className="mt-2 text-sm text-slate-500">
                {report
                  ? `Range: ${report.range.label} • ${includeOffline ? 'All settlements (Bank + Offline)' : 'Bank rails only (Astra)'}`
                  : 'Live operational view across sessions, payments, groups, and recurring behavior.'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <FilterSelect label="Range" value={preset} onChange={(value) => setPreset(value as RangePreset)}>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="custom">Custom</option>
              </FilterSelect>

              {preset === 'custom' ? (
                <>
                  <FilterDate label="Start" value={startDate} onChange={setStartDate} />
                  <FilterDate label="End" value={endDate} onChange={setEndDate} />
                </>
              ) : null}

              <label className="flex cursor-pointer select-none items-center gap-2.5 rounded-2xl border border-slate-200 bg-slate-50/80 px-3.5 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-100/80">
                <span>Include Settled Offline?</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={includeOffline}
                  onClick={(e) => {
                    e.preventDefault();
                    setIncludeOffline((prev) => !prev);
                  }}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    includeOffline ? 'bg-emerald-600' : 'bg-slate-300'
                  }`}
                  title="Toggle whether to include settled offline payments"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      includeOffline ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </label>

              <FilterSelect label="Users" value={sortUsersBy} onChange={(value) => setSortUsersBy(value as UserSortBy)}>
                <option value="name">Users: name</option>
                <option value="createdAt">Users: created</option>
              </FilterSelect>

              <FilterSelect label="User dir" value={sortUsersDir} onChange={(value) => setSortUsersDir(value as SortDir)}>
                <option value="asc">A → Z / oldest</option>
                <option value="desc">Z → A / newest</option>
              </FilterSelect>

              <FilterSelect label="Groups" value={sortGroupsBy} onChange={(value) => setSortGroupsBy(value as GroupSortBy)}>
                <option value="name">Groups: name</option>
                <option value="createdAt">Groups: created</option>
                <option value="nextSettlementAt">Groups: next settlement</option>
              </FilterSelect>

              <FilterSelect label="Group dir" value={sortGroupsDir} onChange={(value) => setSortGroupsDir(value as SortDir)}>
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </FilterSelect>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span>Generated {report ? formatDateTime(report.generatedAt) : '—'}</span>
            <span className="text-slate-300">•</span>
            <span>Pending wallet totals stay live and ignore the date filter.</span>
            <button
              onClick={() => void fetchReport()}
              className="rounded-full border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              onClick={() => void handleSignOut()}
              className="rounded-full border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>

        {loadError ? (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-center justify-between gap-3">
            <span>{loadError}</span>
            <button
              onClick={() => void fetchReport()}
              className="text-xs font-semibold underline hover:text-rose-900 shrink-0"
            >
              Retry
            </button>
          </div>
        ) : null}

        {authStatus === 'authorized' && founderPhone ? (
          <>
            <FounderWebImpersonationPanel founderPhone={founderPhone} onImpersonationStarted={onImpersonationStarted} />
            <FboDisbursementAdminPanel founderPhone={founderPhone} />
            <section className="mb-6 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <MerchQrAdminPanel founderPhone={founderPhone} />
            </section>
          </>
        ) : null}

        {loading && !report ? (
          <div className="rounded-[32px] border border-slate-200 bg-white p-12 text-center shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
            <p className="text-sm font-semibold uppercase tracking-[0.26em] text-slate-500">Loading</p>
            <p className="mt-3 text-xl font-black text-slate-950">Pulling sessions, invites, users, and groups…</p>
          </div>
        ) : null}

        {report ? (
          <>
            {/* Critical Alert if platform limit is exceeded */}
            {report.summary.platformDailyLimit?.isCriticalWarning ? (
              <div className="mb-6 flex items-start gap-4 rounded-3xl border-2 border-rose-500 bg-rose-50 p-5 text-rose-950 shadow-lg animate-pulse">
                <div className="rounded-2xl bg-rose-600 p-2.5 text-white shadow-md">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-black text-rose-900 tracking-tight">CRITICAL: Astra Platform Transfer Limit Above 90%</h3>
                    <span className="rounded-full bg-rose-600 px-3 py-0.5 text-xs font-bold text-white uppercase tracking-wider">Danger</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-rose-800 leading-relaxed">
                    Platform daily transfer limit is currently at <span className="font-extrabold underline">{report.summary.platformDailyLimit.usedPercentage.toFixed(1)}%</span> (${(report.summary.platformDailyLimit.usedCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })} / {formatCurrency(report.summary.platformDailyLimit.limitCents ? report.summary.platformDailyLimit.limitCents / 100 : 15000)}). Payments risk failing once the daily limit is exhausted. Contact founders immediately.
                  </p>
                </div>
              </div>
            ) : null}

            {/* Platform Daily Limit Gauge Card */}
            <section className="mb-6 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${report.summary.platformDailyLimit?.isCriticalWarning ? 'bg-rose-500 animate-ping' : 'bg-emerald-500'}`} />
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Platform Limits</p>
                  </div>
                  <h2 className="mt-1 text-2xl font-black text-slate-950">Astra Daily Platform Limit Usage</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Platform daily transfer limit is capped at {formatCurrency(report.summary.platformDailyLimit?.limitCents ? report.summary.platformDailyLimit.limitCents / 100 : 15000)} / day with Astra.
                  </p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Used Today</p>
                    <p className="text-xl font-black text-slate-950">{formatCurrency(report.summary.platformDailyLimit?.usedCents ? report.summary.platformDailyLimit.usedCents / 100 : 0)}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Remaining Capacity</p>
                    <p className={`text-xl font-black ${report.summary.platformDailyLimit?.isCriticalWarning ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {formatCurrency(report.summary.platformDailyLimit?.remainingCents ? report.summary.platformDailyLimit.remainingCents / 100 : 15000)}
                    </p>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="text-right">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Daily Cap</p>
                    <p className="text-xl font-black text-slate-700">{formatCurrency(report.summary.platformDailyLimit?.limitCents ? report.summary.platformDailyLimit.limitCents / 100 : 15000)}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="relative h-4 w-full overflow-hidden rounded-full bg-slate-100 p-0.5">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      (report.summary.platformDailyLimit?.usedPercentage || 0) >= 90
                        ? 'bg-rose-500'
                        : (report.summary.platformDailyLimit?.usedPercentage || 0) >= 70
                        ? 'bg-amber-500'
                        : 'bg-emerald-500'
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, report.summary.platformDailyLimit?.usedPercentage || 0))}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs font-medium text-slate-500">
                  <span>0%</span>
                  <span className="font-bold text-slate-800">{(report.summary.platformDailyLimit?.usedPercentage || 0).toFixed(1)}% Used</span>
                  <span>100% ({formatCurrency(report.summary.platformDailyLimit?.limitCents ? report.summary.platformDailyLimit.limitCents / 100 : 15000)})</span>
                </div>
              </div>
            </section>

            {/* Lifetime Transactions Card with 4 Sub-Tabs */}
            <section className="mb-6 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Lifetime Summary</p>
                  <h2 className="mt-1 text-2xl font-black text-slate-950">Gross Lifetime Transaction Volume & Count</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Total {formatCurrency(report.summary.lifetimeTransactions?.totalGrossVolume || 0)} settled volume across {formatCount(report.summary.lifetimeTransactions?.totalGrossCount || 0)} total payments.
                  </p>
                </div>
                {/* 4 Sub Tabs */}
                <div className="flex flex-wrap items-center gap-1.5 rounded-2xl bg-slate-100 p-1.5 text-xs font-bold text-slate-600">
                  <button
                    onClick={() => setLifetimeTab('bank')}
                    className={`rounded-xl px-3 py-1.5 transition ${lifetimeTab === 'bank' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Bank Payments (Astra)
                  </button>
                  <button
                    onClick={() => setLifetimeTab('offline')}
                    className={`rounded-xl px-3 py-1.5 transition ${lifetimeTab === 'offline' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Settled Offline
                  </button>
                  <button
                    onClick={() => setLifetimeTab('fee')}
                    className={`rounded-xl px-3 py-1.5 transition ${lifetimeTab === 'fee' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Gross Clero Fee
                  </button>
                  <button
                    onClick={() => setLifetimeTab('exemption')}
                    className={`rounded-xl px-3 py-1.5 transition ${lifetimeTab === 'exemption' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Clero+ Fee Exemption
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {lifetimeTab === 'bank' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Astra Bank Volume</p>
                      <p className="mt-2 text-2xl font-black text-emerald-600">{formatCurrency(report.summary.lifetimeTransactions?.bankPayments.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Paid from bank via Astra transfers</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Astra Bank Payment Count</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCount(report.summary.lifetimeTransactions?.bankPayments.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Total settled bank transfers</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Bank Payment Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.lifetimeTransactions?.bankPayments.count || 0) > 0
                          ? formatCurrency((report.summary.lifetimeTransactions?.bankPayments.volume || 0) / report.summary.lifetimeTransactions.bankPayments.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per bank transfer average</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Share of Total Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.lifetimeTransactions?.totalGrossVolume || 0) > 0
                          ? `${(((report.summary.lifetimeTransactions?.bankPayments.volume || 0) / report.summary.lifetimeTransactions.totalGrossVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross settled platform volume</p>
                    </div>
                  </>
                ) : null}

                {lifetimeTab === 'offline' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Settled Offline Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-700">{formatCurrency(report.summary.lifetimeTransactions?.offlineSettled.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Portion settled offline</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Offline Payment Count</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCount(report.summary.lifetimeTransactions?.offlineSettled.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Total offline settlements</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Offline Payment Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.lifetimeTransactions?.offlineSettled.count || 0) > 0
                          ? formatCurrency((report.summary.lifetimeTransactions?.offlineSettled.volume || 0) / report.summary.lifetimeTransactions.offlineSettled.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per offline settlement</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Share of Total Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.lifetimeTransactions?.totalGrossVolume || 0) > 0
                          ? `${(((report.summary.lifetimeTransactions?.offlineSettled.volume || 0) / report.summary.lifetimeTransactions.totalGrossVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross settled platform volume</p>
                    </div>
                  </>
                ) : null}

                {lifetimeTab === 'fee' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Gross Clero Fee Revenue</p>
                      <p className="mt-2 text-2xl font-black text-indigo-600">{formatCurrency(report.summary.lifetimeTransactions?.grossCleroFeeRevenue || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Lifetime collected from Astra payments</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Average Take Rate</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.lifetimeTransactions?.bankPayments.volume || 0) > 0
                          ? `${(((report.summary.lifetimeTransactions?.grossCleroFeeRevenue || 0) / report.summary.lifetimeTransactions.bankPayments.volume) * 100).toFixed(2)}%`
                          : '0.00%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Effective take rate on Astra volume</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Fee-Generating Payments</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {formatCount(Math.max(0, (report.summary.lifetimeTransactions?.bankPayments.count || 0) - (report.summary.lifetimeTransactions?.cleroPlusExemptCount || 0)))}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Payments that paid Clero platform fee</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Fee Per Paid Tx</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {((report.summary.lifetimeTransactions?.bankPayments.count || 0) - (report.summary.lifetimeTransactions?.cleroPlusExemptCount || 0)) > 0
                          ? formatCurrency((report.summary.lifetimeTransactions?.grossCleroFeeRevenue || 0) / (report.summary.lifetimeTransactions.bankPayments.count - report.summary.lifetimeTransactions.cleroPlusExemptCount))
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per fee-paying payment</p>
                    </div>
                  </>
                ) : null}

                {lifetimeTab === 'exemption' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Clero+ Exemption Rate</p>
                      <p className="mt-2 text-2xl font-black text-amber-600">{(report.summary.lifetimeTransactions?.cleroPlusExemptionRate || 0).toFixed(1)}%</p>
                      <p className="mt-1 text-xs text-slate-500">Payments with 0 fee due to Clero+</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Waived Transactions</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCount(report.summary.lifetimeTransactions?.cleroPlusExemptCount || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Total payments with waived Clero fees</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Active Subscribers</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCount(report.summary.cleroPlus?.activeSubscribersCount || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Users enjoying 0% transaction fees</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Monthly Sub Revenue</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCurrency(report.summary.cleroPlus?.monthlyRevenue || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Recurring subscription MRR</p>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            {/* Core Operation Cards Grid */}
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <SummaryCard label="Open Splits" value={formatCount(report.summary.totalOpenSplits)} note="Created in range and not yet completed." source="`sessions/{sessionId}` -> `createdAt`, `status`, `completedAt`" onClick={() => openMetricDetail({ title: 'Open splits', subtitle: 'Sessions created in the selected range that are still open.', source: "`sessions/{sessionId}` -> `createdAt`, `status`, `completedAt`", items: report.openSplitsDetails, itemKind: 'organized_split' })} />
              <SummaryCard label="Settled Splits" value={formatCount(report.summary.totalSettledSplits)} note="Completed during the selected range." source="`sessions/{sessionId}` -> `completedAt`, `status`" onClick={() => openMetricDetail({ title: 'Settled splits', subtitle: 'Sessions that completed during the selected range.', source: "`sessions/{sessionId}` -> `completedAt`, `status`", items: report.settledSplitsDetails, itemKind: 'organized_split' })} />
              
              {/* Identity Verification Status Card (Replaces Pending Connect) */}
              <div
                onClick={() => setShowIdentityDetailModal(true)}
                className="cursor-pointer rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.06)] transition hover:border-slate-300 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Identity Verification</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">Inspect list</span>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <p className="text-3xl font-black text-emerald-600">{formatCount(report.summary.identityVerification?.verifiedCount || 0)}</p>
                  <span className="text-xs font-bold text-slate-500">Verified</span>
                  <span className="text-slate-300">•</span>
                  <p className="text-xl font-black text-amber-600">{formatCount(report.summary.identityVerification?.nonVerifiedCount || 0)}</p>
                  <span className="text-xs font-semibold text-slate-500">Unverified</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1 text-[10px] font-bold">
                  <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-amber-800">Doc: {report.summary.identityVerification?.documentRequiredCount || 0}</span>
                  <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-blue-800">Pending: {report.summary.identityVerification?.pendingCount || 0}</span>
                  <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-rose-800">Rej: {report.summary.identityVerification?.rejectedCount || 0}</span>
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-700">Not Start: {report.summary.identityVerification?.notStartedCount || 0}</span>
                </div>
              </div>

              {/* Clero+ Subscribers & Revenue Card */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Clero+ Subs</span>
                  <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5 text-[10px] font-bold">
                    <button
                      onClick={() => setCleroPlusPeriod('monthly')}
                      className={`rounded-md px-1.5 py-0.5 transition ${cleroPlusPeriod === 'monthly' ? 'bg-white text-slate-950 shadow-xs' : 'text-slate-500'}`}
                    >
                      Mo
                    </button>
                    <button
                      onClick={() => setCleroPlusPeriod('quarterly')}
                      className={`rounded-md px-1.5 py-0.5 transition ${cleroPlusPeriod === 'quarterly' ? 'bg-white text-slate-950 shadow-xs' : 'text-slate-500'}`}
                    >
                      Qtr
                    </button>
                    <button
                      onClick={() => setCleroPlusPeriod('yearly')}
                      className={`rounded-md px-1.5 py-0.5 transition ${cleroPlusPeriod === 'yearly' ? 'bg-white text-slate-950 shadow-xs' : 'text-slate-500'}`}
                    >
                      Yr
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div>
                    <p className="text-3xl font-black text-slate-950">{formatCount(report.summary.cleroPlus?.activeSubscribersCount || 0)}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{formatCount(report.summary.cleroPlus?.trialingCount || 0)} trialing</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-black text-indigo-600">
                      {cleroPlusPeriod === 'monthly'
                        ? formatCurrency(report.summary.cleroPlus?.monthlyRevenue || 0)
                        : cleroPlusPeriod === 'quarterly'
                        ? formatCurrency(report.summary.cleroPlus?.quarterlyRevenue || 0)
                        : formatCurrency(report.summary.cleroPlus?.yearlyRevenue || 0)}
                    </p>
                    <p className="text-[10px] uppercase font-bold text-slate-400">{cleroPlusPeriod} Rev</p>
                  </div>
                </div>
              </div>

              {/* Connected Gmail Card */}
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Connected Gmail</span>
                <p className="mt-3 text-3xl font-black text-slate-950">{formatCount(report.summary.gmailConnectedUsersCount || 0)}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {(report.summary.totalTrackedUsers || 0) > 0
                    ? `${(((report.summary.gmailConnectedUsersCount || 0) / report.summary.totalTrackedUsers) * 100).toFixed(1)}% of user base`
                    : '0% of user base'}
                </p>
              </div>
            </section>

            {/* Requests Generated Tracker with 4 Sub-Tabs & Gross Tracked Volume */}
            <section className="mt-6 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Request Analytics</p>
                  <h2 className="mt-1 text-2xl font-black text-slate-950">Requests Generated & Gross Tracked Volume</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Gross tracked volume: <span className="font-bold text-slate-900">{formatCurrency(report.summary.requestGeneration?.totalGrossTrackedVolume || 0)}</span> across {formatCount(report.summary.requestGeneration?.totalGrossTrackedCount || 0)} total requests & group expenses (all statuses included).
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 rounded-2xl bg-slate-100 p-1.5 text-xs font-bold text-slate-600">
                  <button
                    onClick={() => setRequestsTab('card')}
                    className={`rounded-xl px-3 py-1.5 transition ${requestsTab === 'card' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Credit Card Txns
                  </button>
                  <button
                    onClick={() => setRequestsTab('gmail')}
                    className={`rounded-xl px-3 py-1.5 transition ${requestsTab === 'gmail' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Gmail Receipts
                  </button>
                  <button
                    onClick={() => setRequestsTab('manual')}
                    className={`rounded-xl px-3 py-1.5 transition ${requestsTab === 'manual' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    Manual Entries
                  </button>
                  <button
                    onClick={() => setRequestsTab('photo')}
                    className={`rounded-xl px-3 py-1.5 transition ${requestsTab === 'photo' ? 'bg-white text-slate-950 shadow-sm' : 'hover:text-slate-950'}`}
                  >
                    AI Photo Receipts
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {requestsTab === 'card' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Card Requests Count</p>
                      <p className="mt-2 text-2xl font-black text-blue-600">{formatCount(report.summary.requestGeneration?.creditCard.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">From imported credit card transactions</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Card Tracked Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCurrency(report.summary.requestGeneration?.creditCard.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Gross transaction amount</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Card Request Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.creditCard.count || 0) > 0
                          ? formatCurrency((report.summary.requestGeneration?.creditCard.volume || 0) / report.summary.requestGeneration.creditCard.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per card transaction average</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Volume Share</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.totalGrossTrackedVolume || 0) > 0
                          ? `${(((report.summary.requestGeneration?.creditCard.volume || 0) / report.summary.requestGeneration.totalGrossTrackedVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross tracked request volume</p>
                    </div>
                  </>
                ) : null}

                {requestsTab === 'gmail' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Gmail Requests Count</p>
                      <p className="mt-2 text-2xl font-black text-red-600">{formatCount(report.summary.requestGeneration?.gmailReceipts.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">From parsed Gmail receipt emails</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Gmail Tracked Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCurrency(report.summary.requestGeneration?.gmailReceipts.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Gross receipt amount</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Gmail Receipt Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.gmailReceipts.count || 0) > 0
                          ? formatCurrency((report.summary.requestGeneration?.gmailReceipts.volume || 0) / report.summary.requestGeneration.gmailReceipts.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per parsed receipt average</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Volume Share</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.totalGrossTrackedVolume || 0) > 0
                          ? `${(((report.summary.requestGeneration?.gmailReceipts.volume || 0) / report.summary.requestGeneration.totalGrossTrackedVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross tracked request volume</p>
                    </div>
                  </>
                ) : null}

                {requestsTab === 'manual' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Manual Requests Count</p>
                      <p className="mt-2 text-2xl font-black text-slate-700">{formatCount(report.summary.requestGeneration?.manual.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">User entered manually</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Manual Tracked Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCurrency(report.summary.requestGeneration?.manual.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Gross manual request amount</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Manual Request Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.manual.count || 0) > 0
                          ? formatCurrency((report.summary.requestGeneration?.manual.volume || 0) / report.summary.requestGeneration.manual.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per manual split average</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Volume Share</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.totalGrossTrackedVolume || 0) > 0
                          ? `${(((report.summary.requestGeneration?.manual.volume || 0) / report.summary.requestGeneration.totalGrossTrackedVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross tracked request volume</p>
                    </div>
                  </>
                ) : null}

                {requestsTab === 'photo' ? (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">AI Photo Requests Count</p>
                      <p className="mt-2 text-2xl font-black text-purple-600">{formatCount(report.summary.requestGeneration?.aiPhotoReceipts.count || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Scanned and parsed via AI</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">AI Photo Tracked Volume</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">{formatCurrency(report.summary.requestGeneration?.aiPhotoReceipts.volume || 0)}</p>
                      <p className="mt-1 text-xs text-slate-500">Gross scanned receipt amount</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Photo Receipt Size</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.aiPhotoReceipts.count || 0) > 0
                          ? formatCurrency((report.summary.requestGeneration?.aiPhotoReceipts.volume || 0) / report.summary.requestGeneration.aiPhotoReceipts.count)
                          : '$0.00'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Per AI scanned receipt average</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Volume Share</p>
                      <p className="mt-2 text-2xl font-black text-slate-950">
                        {(report.summary.requestGeneration?.totalGrossTrackedVolume || 0) > 0
                          ? `${(((report.summary.requestGeneration?.aiPhotoReceipts.volume || 0) / report.summary.requestGeneration.totalGrossTrackedVolume) * 100).toFixed(1)}%`
                          : '0.0%'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Of gross tracked request volume</p>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            {/* Account Stats & User Concentration Grid */}
            <section className="mt-6 grid gap-4 md:grid-cols-2">
              {/* Card & Bank Coverage Card */}
              <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Account Coverage</p>
                <h2 className="mt-1 text-xl font-black text-slate-950">Card & Bank Account Metrics</h2>
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Avg Cards / User</p>
                    <p className="mt-1 text-2xl font-black text-slate-950">{(report.summary.accountStats?.averageCardsPerUser || 0).toFixed(2)}</p>
                    <p className="mt-1 text-xs text-slate-500">{formatCount(report.summary.accountStats?.totalCardAccounts || 0)} total linked cards</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">No Credit Cards</p>
                    <p className="mt-1 text-2xl font-black text-amber-600">{(report.summary.accountStats?.zeroCardsPercentage || 0).toFixed(1)}%</p>
                    <p className="mt-1 text-xs text-slate-500">{formatCount(Math.max(0, (report.summary.totalTrackedUsers || 0) - (report.summary.accountStats?.usersWithCardsCount || 0)))} users without cards</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">No Bank Accounts</p>
                    <p className="mt-1 text-2xl font-black text-rose-600">{(report.summary.accountStats?.zeroBanksPercentage || 0).toFixed(1)}%</p>
                    <p className="mt-1 text-xs text-slate-500">{formatCount(Math.max(0, (report.summary.totalTrackedUsers || 0) - (report.summary.accountStats?.usersWithBanksCount || 0)))} users without banks</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total Bank Accounts</p>
                    <p className="mt-1 text-2xl font-black text-slate-950">{formatCount(report.summary.accountStats?.totalBankAccounts || 0)}</p>
                    <p className="mt-1 text-xs text-slate-500">{formatCount(report.summary.accountStats?.usersWithBanksCount || 0)} users with connected bank</p>
                  </div>
                </div>
              </div>

              {/* User Payment & Request Concentration (Pareto Indicator) */}
              <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Distribution</p>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                    report.summary.userConcentration?.isConcentrated
                      ? 'bg-amber-100 text-amber-900 border border-amber-200'
                      : 'bg-teal-100 text-teal-900 border border-teal-200'
                  }`}>
                    {report.summary.userConcentration?.isConcentrated ? 'Power-User Concentrated' : 'Evenly Distributed'}
                  </span>
                </div>
                <h2 className="mt-1 text-xl font-black text-slate-950">Activity Concentration</h2>
                <p className="mt-1 text-sm text-slate-600">{report.summary.userConcentration?.summaryLabel || '—'}</p>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Top 10% Share</p>
                    <p className="mt-1 text-2xl font-black text-slate-950">{(report.summary.userConcentration?.top10PercentShare || 0).toFixed(1)}%</p>
                    <p className="mt-1 text-xs text-slate-500">Of total payment & split activity</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Top 20% Share</p>
                    <p className="mt-1 text-2xl font-black text-slate-950">{(report.summary.userConcentration?.top20PercentShare || 0).toFixed(1)}%</p>
                    <p className="mt-1 text-xs text-slate-500">Of total payment & split activity</p>
                  </div>
                </div>
                <div className="mt-3 rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600">
                  Top {report.summary.userConcentration?.topUsersCount || 0} power users generated {formatCurrency(report.summary.userConcentration?.topUsersVolume || 0)} in combined volume.
                </div>
              </div>
            </section>

            <section className="mt-6 grid gap-4 md:grid-cols-2 2xl:grid-cols-5">
              <SummaryCard label="Active Paying Users" value={formatCount(report.summary.activePayingUsers)} note="Unique users with at least one successful payment in range." source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, success timestamps, `phone`" onClick={() => openMetricDetail({ title: 'Active paying users', subtitle: 'Unique payer accounts with at least one successful payment event in the selected range.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, success timestamps, `phone`", rows: buildTopPayerRows(report.users) })} />
              <SummaryCard label="Avg Payments / User" value={report.summary.activePayingUsers > 0 ? report.summary.averagePaymentsPerUser.toFixed(2) : '0.00'} note={`${formatCount(report.paymentsDone.length)} successful payments across ${formatCount(report.summary.activePayingUsers)} paying users.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` successful payment events grouped by `phone`" onClick={() => openMetricDetail({ title: 'Average payments per user', subtitle: 'Successful payment events divided by unique paying users in the selected range.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` successful payment events grouped by `phone`", rows: [{ label: 'Successful payments', value: formatCount(report.paymentsDone.length) }, { label: 'Active paying users', value: formatCount(report.summary.activePayingUsers) }, { label: 'Average payments / user', value: report.summary.activePayingUsers > 0 ? report.summary.averagePaymentsPerUser.toFixed(2) : '0.00' }] })} />
              <SummaryCard label="Avg Volume / User" value={formatCurrency(report.summary.averagePaymentVolumePerUser)} note={`${formatCurrency(report.summary.totalPaymentsDoneAmount)} total successful payer volume in range.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` successful payment totals grouped by `phone`" onClick={() => openMetricDetail({ title: 'Average payment volume per user', subtitle: 'Successful payment volume divided by unique paying users in the selected range.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` successful payment totals grouped by `phone`", rows: [{ label: 'Successful payment volume', value: formatCurrency(report.summary.totalPaymentsDoneAmount) }, { label: 'Active paying users', value: formatCount(report.summary.activePayingUsers) }, { label: 'Average volume / user', value: formatCurrency(report.summary.averagePaymentVolumePerUser) }] })} />
              <SummaryCard label="Monthly Payment Volume Growth" value={formatPercentDelta(report.summary.monthlyPaymentVolumeGrowthRate)} note={`${formatCurrency(report.summary.current30dPaymentVolume)} current 30d vs ${formatCurrency(report.summary.previous30dPaymentVolume)} previous 30d.`} source="Rolling 30-day successful payment volume from canonical `sessions/{sessionId}/invites/{inviteId}` where `status in {'paid','verified','settled_offline'}`" onClick={() => openMetricDetail({ title: 'Monthly payment volume growth', subtitle: 'Rolling 30-day successful payment volume growth compared with the prior 30-day window.', source: "Rolling 30-day successful payment volume from canonical `sessions/{sessionId}/invites/{inviteId}` where `status in {'paid','verified','settled_offline'}`", rows: [{ label: 'Current 30-day payment volume', value: formatCurrency(report.summary.current30dPaymentVolume) }, { label: 'Previous 30-day payment volume', value: formatCurrency(report.summary.previous30dPaymentVolume) }, { label: 'Growth rate', value: formatPercentDelta(report.summary.monthlyPaymentVolumeGrowthRate) }] })} />
              <SummaryCard label="Monthly User Signup Growth" value={formatPercentDelta(report.summary.monthlyUserSignupGrowthRate)} note={`${formatCount(report.summary.current30dUserSignups)} current 30d vs ${formatCount(report.summary.previous30dUserSignups)} previous 30d.`} source="Rolling 30-day user signup counts from `users/{phone}` -> `createdAt`, with `profiles/{phone}.createdAt` fallback when needed" onClick={() => openMetricDetail({ title: 'Monthly user signup growth', subtitle: 'Rolling 30-day user signup growth compared with the prior 30-day window.', source: "`users/{phone}` -> `createdAt`, with `profiles/{phone}.createdAt` fallback when needed", rows: [{ label: 'Current 30-day user signups', value: formatCount(report.summary.current30dUserSignups) }, { label: 'Previous 30-day user signups', value: formatCount(report.summary.previous30dUserSignups) }, { label: 'Growth rate', value: formatPercentDelta(report.summary.monthlyUserSignupGrowthRate) }] })} />
            </section>

            <section className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
              <SummaryCard label="Successful Payments" value={formatCompactCurrency(report.summary.totalPaymentsDoneAmount)} note={`${formatCount(report.paymentsDone.length)} successful events • ${formatCount(report.summary.instantPayoutPaymentsCount)} instant • ${formatCount(report.summary.forceSettlementPaymentsCount)} force.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `phone`, success timestamps, `amount`, `instantPayoutDecision`, `instantPayoutApplied`, `instantTransferCreatedAt`" onClick={() => openMetricDetail({ title: 'Successful payments', subtitle: 'Successful payment events from the shared canonical invite settlement stream.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `phone`, success timestamps, `amount`, `instantPayoutDecision`, `instantPayoutApplied`, `instantTransferCreatedAt`", rows: buildPaymentFlowRows({ instantCount: report.summary.instantPayoutPaymentsCount, instantAmount: report.summary.instantPayoutPaymentsAmount, forceCount: report.summary.forceSettlementPaymentsCount, forceAmount: report.summary.forceSettlementPaymentsAmount }), items: report.paymentsDone })} />
              <SummaryCard label="Unsettled Volume" value={formatCompactCurrency(report.summary.unsettledVolume)} note={`${formatCount(report.unsettledVolumeItems.length)} unpaid/open invite rows still awaiting payment.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> open/unpaid `status`, invite/session creation timestamps, `amount`" onClick={() => openMetricDetail({ title: 'Unpaid / open volume', subtitle: 'Canonical invite volume that is still open and has not yet been paid.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> open/unpaid `status`, invite/session creation timestamps, `amount`", rows: [{ label: 'Unsettled volume', value: formatCurrency(report.summary.unsettledVolume) }, { label: 'Contributing unpaid rows', value: formatCount(report.unsettledVolumeItems.length) }, { label: 'Pending connect snapshot', value: formatCurrency(report.summary.totalPendingConnect) }, { label: 'Pending platform snapshot', value: formatCurrency(report.summary.totalPendingPlatform) }], items: report.unsettledVolumeItems })} />
              <SummaryCard label="Total Revenue" value={formatCompactCurrency(report.summary.totalRevenue)} note="Selected-range payee fees plus collected organizer fees." source="Canonical `sessions/{sessionId}/invites/{inviteId}` fee deltas + `sessions/{sessionId}` -> `organizerFeeCollected`, `organizerFeeAmount`, `organizerFeeCollectedAt`" onClick={() => openMetricDetail({ title: 'Total revenue', subtitle: 'Revenue collected in the selected range from payee fees and organizer fees.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` fee deltas + `sessions/{sessionId}` -> `organizerFeeCollected`, `organizerFeeAmount`, `organizerFeeCollectedAt`", rows: [{ label: 'Selected-range revenue', value: formatCurrency(report.summary.totalRevenue) }, { label: 'Current 30-day revenue', value: formatCurrency(report.summary.current30dRevenue) }, { label: 'Previous 30-day revenue', value: formatCurrency(report.summary.previous30dRevenue) }] })} />
              <SummaryCard label="Expected Revenue" value={formatCompactCurrency(report.summary.expectedRevenue)} note={`${formatCount(report.expectedRevenueItems.length)} unpaid/open fee rows plus uncollected organizer fees.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` unpaid/open fee deltas + `sessions/{sessionId}` -> `organizerFeeAmount`, `organizerFeeCollected`" onClick={() => openMetricDetail({ title: 'Expected revenue', subtitle: 'Forward-looking fee pipeline from unpaid canonical invites and uncollected organizer fees.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` unpaid/open fee deltas + `sessions/{sessionId}` -> `organizerFeeAmount`, `organizerFeeCollected`", rows: [{ label: 'Expected revenue', value: formatCurrency(report.summary.expectedRevenue) }, { label: 'Contributing fee rows', value: formatCount(report.expectedRevenueItems.length) }, { label: 'Realized revenue', value: formatCurrency(report.summary.totalRevenue) }], items: report.expectedRevenueItems })} />
              <SummaryCard label="Revenue Growth" value={formatPercentDelta(report.summary.totalRevenueGrowthRate)} note={`${formatCurrency(report.summary.current30dRevenue)} current 30d vs ${formatCurrency(report.summary.previous30dRevenue)} previous 30d.`} source="Rolling 30-day revenue from canonical invite fee deltas and collected organizer fees" onClick={() => openMetricDetail({ title: 'Revenue growth', subtitle: 'Rolling 30-day revenue growth compared with the prior 30-day window.', source: "Rolling 30-day revenue from canonical invite fee deltas and collected organizer fees", rows: [{ label: 'Current 30-day revenue', value: formatCurrency(report.summary.current30dRevenue) }, { label: 'Previous 30-day revenue', value: formatCurrency(report.summary.previous30dRevenue) }, { label: 'Growth rate', value: formatPercentDelta(report.summary.totalRevenueGrowthRate) }] })} />
            </section>

            <section className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-2">
              <SummaryCard label="Recurring Auto-pay" value={formatCount(report.summary.recurringAutoPayEnabledInvites)} note={`${formatCount(report.summary.recurringOptedOutInvites)} opted out.`} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`, `recurringOptOut`, `status='declined_recurring'`" onClick={() => openMetricDetail({ title: 'Recurring auto-pay', subtitle: 'Invites with recurring auto-pay enabled.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`, `recurringOptOut`, `status='declined_recurring'`", items: report.recurringAutoPayInviteItems })} />
              <SummaryCard label="Recurring Parents" value={formatCount(report.summary.recurringParentSessions)} note={`${formatCount(report.summary.recurringChildSessions)} generated child sessions.`} source="`sessions/{sessionId}` -> `isRecurring`, `recurringParentSessionId`, `recurringGeneratedCount`" onClick={() => openMetricDetail({ title: 'Recurring parent sessions', subtitle: 'Parent sessions that generated recurring children.', source: "`sessions/{sessionId}` -> `isRecurring`, `recurringParentSessionId`, `recurringGeneratedCount`", items: report.recurringParentSessionItems, itemKind: 'organized_split' })} />
            </section>

            <section className="mt-8 grid gap-4 xl:grid-cols-5">
              <MetricChartCard
                title="Sessions created"
                value={formatCount(topSeries.reduce((sum, point) => sum + point.sessionsCreated, 0))}
                subtitle="New split sessions created per day"
                source="`sessions/{sessionId}` -> `createdAt`"
                chart={<BarTrend values={topSeries.map((point) => point.sessionsCreated)} color="#0f766e" />}
                onClick={() => openMetricDetail({ title: 'Sessions created by day', subtitle: 'Daily bucket totals for session creation.', source: "`sessions/{sessionId}` -> `createdAt`", rows: buildSeriesRows(topSeries, (point) => point.sessionsCreated) })}
              />
              <MetricChartCard
                title="Sessions completed"
                value={formatCount(topSeries.reduce((sum, point) => sum + point.sessionsCompleted, 0))}
                subtitle="Sessions reaching completed status"
                source="`sessions/{sessionId}` -> `completedAt`, `status`"
                chart={<BarTrend values={topSeries.map((point) => point.sessionsCompleted)} color="#2563eb" />}
                onClick={() => openMetricDetail({ title: 'Sessions completed by day', subtitle: 'Daily bucket totals for completed sessions.', source: "`sessions/{sessionId}` -> `completedAt`, `status`", rows: buildSeriesRows(topSeries, (point) => point.sessionsCompleted) })}
              />
              <MetricChartCard
                title="Successful payments"
                value={formatCount(topSeries.reduce((sum, point) => sum + point.successfulPayments, 0))}
                subtitle="Paid, verified, and offline-settled invites"
                source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `paidAt`, `settledOfflineAt`, `verifiedAt`"
                chart={<BarTrend values={topSeries.map((point) => point.successfulPayments)} color="#ea580c" />}
                onClick={() => openMetricDetail({ title: 'Successful payments by day', subtitle: 'Daily bucket totals for successful invite settlements.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `paidAt`, `settledOfflineAt`, `verifiedAt`", rows: buildSeriesRows(topSeries, (point) => point.successfulPayments) })}
              />
              <MetricChartCard
                title="Payment volume"
                value={formatCompactCurrency(topSeries.reduce((sum, point) => sum + point.paymentVolume, 0))}
                subtitle="Total successful payment volume"
                source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `amount` or applied total fields via invite totals"
                chart={<BarTrend values={topSeries.map((point) => point.paymentVolume)} color="#164e63" />}
                onClick={() => openMetricDetail({ title: 'Payment volume by day', subtitle: 'Daily bucket totals for successful payment volume.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `status in {'paid','verified','settled_offline'}`, `amount` or applied total fields via invite totals", rows: buildSeriesRows(topSeries, (point) => point.paymentVolume, formatCurrency) })}
              />
              <MetricChartCard
                title="Group settlements"
                value={formatCount(topSeries.reduce((sum, point) => sum + point.groupSettlementsCreated, 0))}
                subtitle="Settlement docs created by day"
                source="`groups/{groupId}/settlements/{settlementId}` -> `startedAt`, `createdAt`"
                chart={<BarTrend values={topSeries.map((point) => point.groupSettlementsCreated)} color="#b45309" />}
                onClick={() => openMetricDetail({ title: 'Group settlements', subtitle: 'Settlement documents created in the selected range.', source: "`groups/{groupId}/settlements/{settlementId}` -> `startedAt`, `createdAt`", settlements: report.groupSettlementEvents, rows: buildSeriesRows(topSeries, (point) => point.groupSettlementsCreated) })}
              />
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Waitlist Conversion</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Modal funnel performance</h2>
                  <p className="mt-1 text-sm text-slate-500">Tracks modal opens, submit intent, successful signups, and drop-off patterns.</p>
                </div>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <SummaryCard label="Modal Opens" value={formatCount(waitlistInsights?.summary.modalOpens || 0)} note="Times waitlist modal opened." source="`waitlistFunnelEvents` eventType=modal_opened" />
                <SummaryCard label="Submit Clicks" value={formatCount(waitlistInsights?.summary.submitClicks || 0)} note="Times people tapped the primary CTA." source="`waitlistFunnelEvents` eventType=submit_clicked" />
                <SummaryCard label="Successful Signups" value={formatCount(waitlistInsights?.summary.successfulSignups || 0)} note="Waitlist rows created after modal interaction." source="`waitlist` writes with experiment metadata" />
                <SummaryCard label="Open → Signup" value={formatPercentDelta(waitlistInsights?.summary.openToSignupRate || 0)} note="Signup conversion from modal opens." source="Derived: successfulSignups / modalOpens" />
              </div>
              <div className="mt-5 grid gap-4 xl:grid-cols-3">
                <MetricChartCard
                  title="Funnel opens by day"
                  value={formatCount((waitlistInsights?.timeseries || []).reduce((sum, point) => sum + point.opens, 0))}
                  subtitle="Daily waitlist modal entry volume"
                  source="Daily bucket from waitlist funnel events"
                  chart={<BarTrend values={(waitlistInsights?.timeseries || []).map((point) => point.opens)} color="#0f766e" />}
                />
                <MetricChartCard
                  title="Submit clicks by day"
                  value={formatCount((waitlistInsights?.timeseries || []).reduce((sum, point) => sum + point.submitClicks, 0))}
                  subtitle="Daily CTA click intent"
                  source="Daily submit_clicked event count"
                  chart={<BarTrend values={(waitlistInsights?.timeseries || []).map((point) => point.submitClicks)} color="#2563eb" />}
                />
                <MetricChartCard
                  title="Signups by day"
                  value={formatCount((waitlistInsights?.timeseries || []).reduce((sum, point) => sum + point.signups, 0))}
                  subtitle="Daily successful waitlist joins"
                  source="Daily waitlist document count with experiment metadata"
                  chart={<BarTrend values={(waitlistInsights?.timeseries || []).map((point) => point.signups)} color="#ea580c" />}
                />
              </div>
              <div className="mt-6 grid gap-4 xl:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">CTA Variant Performance</p>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    {(waitlistInsights?.byVariant || []).slice(0, 8).map((row) => (
                      <p key={`wl_var_${row.key}`}>{row.key}: <span className="font-bold text-slate-900">{formatCount(row.signups)}</span> signups • {formatPercentDelta(row.openToSignupRate)}</p>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Device Conversion</p>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    {(waitlistInsights?.byDevice || []).slice(0, 8).map((row) => (
                      <p key={`wl_dev_${row.key}`}>{row.key}: <span className="font-bold text-slate-900">{formatCount(row.signups)}</span> signups • {formatPercentDelta(row.openToSignupRate)}</p>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Trigger Performance</p>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    {(waitlistInsights?.byTrigger || []).slice(0, 8).map((row) => (
                      <p key={`wl_trigger_${row.key}`}>{row.key}: <span className="font-bold text-slate-900">{formatCount(row.signups)}</span> signups • {formatPercentDelta(row.openToSignupRate)}</p>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Attribution</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Campaign performance</h2>
                  <p className="mt-1 text-sm text-slate-500">Focused view of campaign-tagged traffic, conversion, and quality signals.</p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  Campaign stream includes `/` by design
                </div>
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                <SummaryCard
                  label="Attributed Visits"
                  value={formatCount(report.attribution.summary.totalVisits)}
                  note="Campaign-tagged page visits in selected date range."
                  source="`webAttributionEvents` campaign stream"
                />
                <SummaryCard
                  label="Waitlist Signups"
                  value={formatCount(report.attribution.summary.totalWaitlistSignups)}
                  note="Waitlist submissions tied to attributed visitors."
                  source="`waitlist` joined via attribution visitor id"
                />
                <SummaryCard
                  label="Visitor → Waitlist Rate"
                  value={formatPercentDelta(report.attribution.summary.overallConversionRate)}
                  note="Share of unique attributed visitors who joined waitlist."
                  source="derived conversion ratio"
                />
              </div>

              <div className="mt-7">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Deep Dive</p>
                <div className="mt-3 space-y-3">
                  <div className="rounded-2xl border border-slate-200">
                    <button type="button" onClick={() => setOpenDeepDivePanel(openDeepDivePanel === 'campaign' ? null : 'campaign')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                      <span className="text-sm font-semibold text-slate-900">Campaign performance</span>
                      <span className="text-xs text-slate-500">{openDeepDivePanel === 'campaign' ? 'Hide' : 'Show'}</span>
                    </button>
                    {openDeepDivePanel === 'campaign' ? (
                      <div className="border-t border-slate-200 px-4 py-4">
                        <p className="mb-3 text-xs text-slate-500">This view groups campaign traffic by one dimension at a time. `Visits` are event counts, `Waitlist` is conversion count, and `Share` is contribution to all campaign visits in range.</p>
                        <div className="mb-4 flex flex-wrap items-center gap-2">
                          <select value={campaignPanelDimension} onChange={(event) => setCampaignPanelDimension(event.target.value as AttributionPanelDimension)} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm">
                            <option value="source">source</option>
                            <option value="campaign">campaign</option>
                            <option value="medium">medium</option>
                            <option value="content">content</option>
                            <option value="term">term</option>
                            <option value="id">id</option>
                            <option value="referrer">referrer</option>
                            <option value="path">path</option>
                          </select>
                          <button type="button" onClick={() => setCampaignPanelIncludeUnknown((value) => !value)} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700">
                            {campaignPanelIncludeUnknown ? 'Hide unknown' : 'Show unknown'}
                          </button>
                          <button type="button" onClick={() => { setCampaignPanelDimension('source'); setCampaignPanelIncludeUnknown(true); }} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700">
                            Clear panel filters
                          </button>
                        </div>
                        <div className="mb-4 grid gap-4 lg:grid-cols-3">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Traffic Share Pie</p>
                            <div className="mt-3 flex items-center gap-3">
                              <div className="h-20 w-20 rounded-full border border-slate-300" style={{ background: trafficPieTotal > 0 ? `conic-gradient(#0f766e 0 ${(chartRows[0]?.visits || 0) / trafficPieTotal * 360}deg, #2563eb ${(chartRows[0]?.visits || 0) / trafficPieTotal * 360}deg ${((chartRows[0]?.visits || 0) + (chartRows[1]?.visits || 0)) / trafficPieTotal * 360}deg, #ea580c ${((chartRows[0]?.visits || 0) + (chartRows[1]?.visits || 0)) / trafficPieTotal * 360}deg 360deg)` : '#e2e8f0' }} />
                              <div className="text-xs text-slate-600">
                                <p>Top row: {chartRows[0]?.label || '—'}</p>
                                <p>Second: {chartRows[1]?.label || '—'}</p>
                                <p>Others combined</p>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Funnel</p>
                            <p className="mt-2 text-xs text-slate-600">How traffic narrows from visits to unique people to waitlist signups.</p>
                            <div className="mt-3 space-y-2 text-xs text-slate-700">
                              <p>Visits: <span className="font-semibold text-slate-900">{formatCount(funnelVisits)}</span></p>
                              <p>Unique visitors: <span className="font-semibold text-slate-900">{formatCount(funnelVisitors)}</span></p>
                              <p>Waitlist: <span className="font-semibold text-slate-900">{formatCount(funnelWaitlist)}</span></p>
                            </div>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Quality</p>
                            <p className="mt-2 text-xs text-slate-600">Unknown attribution means visits without usable campaign tags.</p>
                            <p className="mt-3 text-sm text-slate-700">Unknown share: <span className="font-semibold text-slate-900">{formatPercentDelta(attributionV2?.qualitySignals.unknownShare || 0)}</span></p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {campaignPanelRows.length === 0 ? <p className="text-sm text-slate-500">No rows for this filter combination.</p> : campaignPanelRows.map((row) => (
                            <div key={`panel_${row.key}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold text-slate-900">{row.label}</p>
                                <p className="text-xs text-slate-600">{formatPercentDelta(row.conversionRate)}</p>
                              </div>
                              <div className="mt-2 h-2 w-full rounded-full bg-slate-200">
                                <div className="h-2 rounded-full bg-teal-600" style={{ width: `${Math.min(100, Math.max(3, row.trafficShare * 100))}%` }} />
                              </div>
                              <p className="mt-2 text-xs text-slate-600">{formatCount(row.visits)} visits • {formatCount(row.waitlistSignups)} waitlist • {formatPercentDelta(row.trafficShare)} share</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-slate-200">
                    <button type="button" onClick={() => setOpenDeepDivePanel(openDeepDivePanel === 'creative' ? null : 'creative')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                      <span className="text-sm font-semibold text-slate-900">Creative diagnostics</span>
                      <span className="text-xs text-slate-500">{openDeepDivePanel === 'creative' ? 'Hide' : 'Show'}</span>
                    </button>
                    {openDeepDivePanel === 'creative' ? (
                      <div className="border-t border-slate-200 px-4 py-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">{topContents.map((row) => <p key={`ct_${row.key}`}>{row.label}: {formatCount(row.visits)}</p>)}</div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">{topTerms.map((row) => <p key={`tm_${row.key}`}>{row.label}: {formatCount(row.visits)}</p>)}</div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">{topIds.map((row) => <p key={`id2_${row.key}`}>{row.label}: {formatCount(row.visits)}</p>)}</div>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-slate-200">
                    <button type="button" onClick={() => setOpenDeepDivePanel(openDeepDivePanel === 'quality' ? null : 'quality')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                      <span className="text-sm font-semibold text-slate-900">Traffic quality</span>
                      <span className="text-xs text-slate-500">{openDeepDivePanel === 'quality' ? 'Hide' : 'Show'}</span>
                    </button>
                    {openDeepDivePanel === 'quality' ? (
                      <div className="border-t border-slate-200 px-4 py-4 grid gap-4 md:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          <p>Repeat rate: <span className="font-semibold text-slate-900">{formatPercentDelta(attributionV2?.qualitySignals.repeatVisitorRate || 0)}</span></p>
                          <p>Single event visitors: <span className="font-semibold text-slate-900">{formatCount(attributionV2?.qualitySignals.repeatVisitorBuckets.singleEvent || 0)}</span></p>
                          <p>2-3 events: <span className="font-semibold text-slate-900">{formatCount(attributionV2?.qualitySignals.repeatVisitorBuckets.repeat2To3 || 0)}</span></p>
                          <p>4+ events: <span className="font-semibold text-slate-900">{formatCount(attributionV2?.qualitySignals.repeatVisitorBuckets.repeat4Plus || 0)}</span></p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                          {(attributionV2?.qualitySignals.referrerMismatches || []).slice(0, 6).map((row) => (
                            <p key={`mm_${row.source}_${row.referrerDomain}`}>{row.source} vs {row.referrerDomain}: <span className="font-semibold text-slate-900">{formatCount(row.visits)}</span></p>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="rounded-2xl border border-slate-200">
                    <button type="button" onClick={() => setOpenDeepDivePanel(openDeepDivePanel === 'journeys' ? null : 'journeys')} className="flex w-full items-center justify-between px-4 py-3 text-left">
                      <span className="text-sm font-semibold text-slate-900">Website journeys</span>
                      <span className="text-xs text-slate-500">{openDeepDivePanel === 'journeys' ? 'Hide' : 'Show'}</span>
                    </button>
                    {openDeepDivePanel === 'journeys' ? (
                      <div className="border-t border-slate-200 px-4 py-4 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {(['all', 'marketing', 'product_flow'] as const).map((value) => (
                            <button key={value} type="button" onClick={() => setCampaignRouteClassFilter(value)} className={`rounded-full px-3 py-1 text-xs font-semibold ${campaignRouteClassFilter === value ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-700'}`}>{value}</button>
                          ))}
                          {(['all', 'tagged', 'untagged', 'waitlist-open'] as const).map((value) => (
                            <button key={value} type="button" onClick={() => setCampaignLandingFilter(value)} className={`rounded-full px-3 py-1 text-xs font-semibold ${campaignLandingFilter === value ? 'bg-teal-700 text-white' : 'border border-slate-300 text-slate-700'}`}>{value}</button>
                          ))}
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                            {routeClassVisibleRows.map((row) => <p key={`rc_${row.key}`}>{row.key}: <span className="font-semibold text-slate-900">{formatCount(row.visits)}</span> visits</p>)}
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                            {landingVisibleRows.map((row) => <p key={`lc_${row.key}`}>{row.key}: <span className="font-semibold text-slate-900">{formatCount(row.visits)}</span> visits</p>)}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <button type="button" onClick={() => setShowDataDiagnostics((value) => !value)} className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Data diagnostics</span>
                  <span className="text-xs text-slate-600">{showDataDiagnostics ? 'Hide' : 'Show'}</span>
                </button>
                {showDataDiagnostics ? (
                  <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <SummaryCard label="Coverage" value={formatPercentDelta(report.attributionCoverage.coverageRate)} note={`${formatCount(report.attributionCoverage.waitlistWithAttribution)} / ${formatCount(report.attributionCoverage.waitlistTotal)} waitlist rows include attribution.`} source="`waitlist` attribution payload coverage" />
                    <SummaryCard label="Unknown Source" value={formatCount(unknownAttributionGroup?.visits || 0)} note={unknownAttributionGroup ? `${formatCount(unknownAttributionGroup.uniqueVisitors)} visitors have no explicit source tag.` : 'No unknown-source campaign rows.'} source="Tagged events with missing source value grouped as `unknown`" />
                    <SummaryCard label="Scanned Attr Events" value={formatCount(report.meta.scannedWebAttributionEvents || 0)} note="Raw attribution docs scanned server-side before date filtering." source="`meta.scannedWebAttributionEvents` from backend report build" />
                    <SummaryCard label="Scanned Waitlist" value={formatCount(report.meta.scannedWaitlist || 0)} note="Waitlist docs scanned server-side before date filtering." source="`meta.scannedWaitlist` from backend report build" />
                  </div>
                ) : null}
              </div>
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Website Visits</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">General website visits (non-campaign)</h2>
                  <p className="mt-1 text-sm text-slate-500">Traffic without campaign/source tagging. Includes path-level breakdown.</p>
                </div>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <SummaryCard label="Unique Visitors" value={formatCount(report.websiteVisits.summary.totalUniqueVisitors)} note="Visitors with non-campaign events in selected range." source="`webAttributionEvents` untagged events -> `visitorId`" />
                <SummaryCard label="Visits" value={formatCount(report.websiteVisits.summary.totalVisits)} note="Total non-campaign events." source="`webAttributionEvents` untagged events -> `eventAt`" />
                <SummaryCard label="Top Path" value={topWebsitePath?.path || '—'} note={topWebsitePath ? `${formatCount(topWebsitePath.visits)} visits • ${formatCount(topWebsitePath.uniqueVisitors)} visitors` : 'No path data in range.'} source="Untagged events grouped by raw path" />
                <MetricChartCard
                  title="Visits by day"
                  value={formatCount(websiteSeries.reduce((sum, point) => sum + point.visits, 0))}
                  subtitle="General non-campaign traffic trend"
                  source="Daily buckets from untagged `webAttributionEvents`"
                  chart={<BarTrend values={websiteSeries.map((point) => point.visits)} color="#334155" />}
                />
              </div>
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <th className="px-3 py-3 font-semibold">Path</th>
                      <th className="px-3 py-3 font-semibold">Visits</th>
                      <th className="px-3 py-3 font-semibold">Unique Visitors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {websitePaths.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-sm text-slate-500" colSpan={3}>No general website visits in this range.</td>
                      </tr>
                    ) : websitePaths.map((row) => (
                      <tr key={row.path} className="border-b border-slate-100">
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{row.path}</td>
                        <td className="px-3 py-4 text-sm text-slate-700">{formatCount(row.visits)}</td>
                        <td className="px-3 py-4 text-sm text-slate-700">{formatCount(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-8 grid gap-4 2xl:grid-cols-2">
              <ListSection title="Payments received" subtitle="Organizer-side view of successful invite settlements." items={report.paymentsReceived} source="Canonical `sessions/{sessionId}/invites/{inviteId}` where `status in {'paid','verified','settled_offline'}` joined to `sessions/{sessionId}`" />
              <ListSection title="Payments done" subtitle="Payer-side view of successful invite settlements." items={report.paymentsDone} source="Canonical `sessions/{sessionId}/invites/{inviteId}` where `status in {'paid','verified','settled_offline'}` joined to `sessions/{sessionId}`" />
              <ListSection title="Splits organized" subtitle="Sessions created during the selected range." items={report.organizedSplits} source="`sessions/{sessionId}` -> `createdAt`, `organizerPhone`, `title`, `status`" itemKind="organized_split" onOpenOrganizedSplit={openOrganizedSplitDetail} />
              <ListSection title="Splits paid" subtitle="Successful payer events tied back to each session." items={report.paidSplits} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> success timestamps + `sessions/{sessionId}`" />
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Per-user</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">User statistics</h2>
                  <p className="mt-1 text-sm text-slate-500">Open the detail panel for graphs, split drill-downs, and recurring behavior.</p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {formatCount(report.users.length)} users
                </div>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <th className="px-3 py-3 font-semibold">Name & Phone</th>
                      <th className="px-3 py-3 font-semibold">Identity</th>
                      <th className="px-3 py-3 font-semibold">Clero+</th>
                      <th className="px-3 py-3 font-semibold">Gmail</th>
                      <th className="px-3 py-3 font-semibold">Created</th>
                      <th className="px-3 py-3 font-semibold">Cards</th>
                      <th className="px-3 py-3 font-semibold">Banks</th>
                      <th className="px-3 py-3 font-semibold">Open</th>
                      <th className="px-3 py-3 font-semibold">Settled</th>
                      <th className="px-3 py-3 font-semibold">Received</th>
                      <th className="px-3 py-3 font-semibold">Done</th>
                      <th className="px-3 py-3 font-semibold">Recurring</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.users.map((user) => {
                      const getIdentityBadgeClass = (status?: string) => {
                        switch (status) {
                          case 'verified':
                            return 'bg-emerald-50 text-emerald-800 border-emerald-200';
                          case 'document_required':
                            return 'bg-amber-50 text-amber-800 border-amber-200';
                          case 'pending':
                            return 'bg-blue-50 text-blue-800 border-blue-200';
                          case 'permanently_rejected':
                            return 'bg-rose-50 text-rose-800 border-rose-200';
                          default:
                            return 'bg-slate-100 text-slate-600 border-slate-200';
                        }
                      };

                      return (
                        <tr
                          key={user.phone}
                          className="cursor-pointer border-b border-slate-100 transition hover:bg-slate-50"
                          onClick={() => {
                            setSelectedGroup(null);
                            setSelectedUser(user);
                          }}
                        >
                          <td className="px-3 py-4">
                            <div>
                              <p className="font-bold text-slate-950">{user.name}</p>
                              <p className="text-xs text-slate-500">{user.phone}</p>
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold ${getIdentityBadgeClass(user.identityStatus)}`}>
                              {user.identityStatusLabel || 'Not Started'}
                            </span>
                          </td>
                          <td className="px-3 py-4">
                            {user.isCleroPlus ? (
                              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-bold text-indigo-700 border border-indigo-100">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-4">
                            {user.hasGmailConnected ? (
                              <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700 border border-rose-100">
                                Linked
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-4 text-sm text-slate-600">{formatDate(user.createdAt)}</td>
                          <td className="px-3 py-4 text-sm font-semibold text-slate-800">{formatCount(user.cardCount ?? user.connectedCardCount ?? 0)}</td>
                          <td className="px-3 py-4 text-sm font-semibold text-slate-800">{formatCount(user.bankCount ?? (user.hasPayoutAccount ? 1 : 0))}</td>
                          <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCount(user.openSplitsOrganized)}</td>
                          <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCount(user.settledSplitsOrganized)}</td>
                          <td className="px-3 py-4 text-sm text-slate-600">{formatCurrency(user.paymentsReceivedAmount)}</td>
                          <td className="px-3 py-4 text-sm text-slate-600">{formatCurrency(user.paymentsDoneAmount)}</td>
                          <td className="px-3 py-4 text-sm text-slate-600">
                            {formatCount(user.recurringParentSplitCount)} / {formatCount(user.recurringAutoPayEnabledCount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Per-group</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Group statistics</h2>
                  <p className="mt-1 text-sm text-slate-500">Track settlement cycles, split status, and group-attributed payment flow.</p>
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {formatCount(report.groups.length)} groups
                </div>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.2em] text-slate-500">
                      <th className="px-3 py-3 font-semibold">Group</th>
                      <th className="px-3 py-3 font-semibold">Type</th>
                      <th className="px-3 py-3 font-semibold">Created</th>
                      <th className="px-3 py-3 font-semibold">Next</th>
                      <th className="px-3 py-3 font-semibold">Expenses</th>
                      <th className="px-3 py-3 font-semibold">Settlements</th>
                      <th className="px-3 py-3 font-semibold">Open</th>
                      <th className="px-3 py-3 font-semibold">Settled</th>
                      <th className="px-3 py-3 font-semibold">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.groups.map((group) => (
                      <tr
                        key={group.id}
                        className="cursor-pointer border-b border-slate-100 transition hover:bg-slate-50"
                        onClick={() => {
                          setSelectedUser(null);
                          setSelectedGroup(group);
                        }}
                      >
                        <td className="px-3 py-4">
                          <p className="font-bold text-slate-950">{group.name}</p>
                          <p className="text-xs text-slate-500">{group.id}</p>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-600">{labelizeStatus(group.groupType)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{formatDate(group.createdAt)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{group.nextSettlementLabel}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{formatCurrency(group.currentExpensesTotal)}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCount(group.settlementCount)}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCount(group.openSettlementSplits)}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCount(group.settledSettlementSplits)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{formatCurrency(group.paymentsReceivedAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Recurring</p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">Recurring split info</h2>
                  <p className="mt-1 text-sm text-slate-500">Top-line recurring health pulled directly from live session and invite data.</p>
                </div>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <DetailMetric label="Parent sessions" value={formatCount(report.summary.recurringParentSessions)} source="`sessions/{sessionId}` -> `isRecurring`, no `recurringParentSessionId`" onClick={() => openMetricDetail({ title: 'Recurring parent sessions', subtitle: 'Parent sessions with recurring enabled.', source: "`sessions/{sessionId}` -> `isRecurring`, no `recurringParentSessionId`", items: report.recurringParentSessionItems, itemKind: 'organized_split' })} />
                <DetailMetric label="Generated children" value={formatCount(report.summary.recurringChildSessions)} source="`sessions/{sessionId}` -> `recurringParentSessionId`" onClick={() => openMetricDetail({ title: 'Generated recurring children', subtitle: 'Child sessions created from recurring parents.', source: "`sessions/{sessionId}` -> `recurringParentSessionId`", items: report.recurringChildSessionItems, itemKind: 'organized_split' })} />
                <DetailMetric label="Auto-pay on" value={formatCount(report.summary.recurringAutoPayEnabledInvites)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`" onClick={() => openMetricDetail({ title: 'Recurring auto-pay enabled', subtitle: 'Invite rows where recurring auto-pay is on.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`", items: report.recurringAutoPayInviteItems })} />
                <DetailMetric label="Opted out" value={formatCount(report.summary.recurringOptedOutInvites)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringOptOut`, `status='declined_recurring'`" onClick={() => openMetricDetail({ title: 'Recurring opt-outs', subtitle: 'Invite rows that opted out of recurring charging.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringOptOut`, `status='declined_recurring'`", items: report.recurringOptOutInviteItems })} />
              </div>
            </section>

            <section className="mt-8 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)]">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Scan metadata</p>
              <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-9">
                <DetailMetric label="Sessions" value={formatCount(report.meta.scannedSessions)} source="Read from `sessions`" onClick={() => openMetricDetail({ title: 'Session scan metadata', subtitle: 'Collection scan count for this report run.', source: 'Read from `sessions`', rows: [{ label: 'Collection', value: 'sessions' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedSessions) }] })} />
                <DetailMetric label="Invites counted" value={formatCount(report.meta.scannedCanonicalInvites)} source="Counted from canonical `sessions/{sessionId}/invites/{inviteId}` after scanning `collectionGroup('invites')`" onClick={() => openMetricDetail({ title: 'Invite scan metadata', subtitle: 'Raw collection-group scans and canonical invite docs counted for this report run.', source: "Scanned `collectionGroup('invites')`, counted canonical `sessions/{sessionId}/invites/{inviteId}`", rows: [{ label: 'Collection group', value: 'invites' }, { label: 'Raw invite docs scanned', value: formatCount(report.meta.scannedInvites) }, { label: 'Canonical invite docs counted', value: formatCount(report.meta.scannedCanonicalInvites) }] })} />
                <DetailMetric label="Users" value={formatCount(report.meta.scannedUsers)} source="Read from `users`" onClick={() => openMetricDetail({ title: 'User scan metadata', subtitle: 'Collection scan count for this report run.', source: 'Read from `users`', rows: [{ label: 'Collection', value: 'users' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedUsers) }] })} />
                <DetailMetric label="Profiles" value={formatCount(report.meta.scannedProfiles)} source="Read from `profiles`" onClick={() => openMetricDetail({ title: 'Profile scan metadata', subtitle: 'Collection scan count for this report run.', source: 'Read from `profiles`', rows: [{ label: 'Collection', value: 'profiles' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedProfiles) }] })} />
                <DetailMetric label="Groups" value={formatCount(report.meta.scannedGroups)} source="Read from `groups`" onClick={() => openMetricDetail({ title: 'Group scan metadata', subtitle: 'Collection scan count for this report run.', source: 'Read from `groups`', rows: [{ label: 'Collection', value: 'groups' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedGroups) }] })} />
                <DetailMetric label="Expenses" value={formatCount(report.meta.scannedGroupExpenses)} source="Read from `groups/{groupId}/expenses`" onClick={() => openMetricDetail({ title: 'Group expense scan metadata', subtitle: 'Expense docs scanned across all groups.', source: 'Read from `groups/{groupId}/expenses`', rows: [{ label: 'Subcollection', value: 'groups/{groupId}/expenses' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedGroupExpenses) }] })} />
                <DetailMetric label="Settlements" value={formatCount(report.meta.scannedSettlements)} source="Read from `groups/{groupId}/settlements`" onClick={() => openMetricDetail({ title: 'Settlement scan metadata', subtitle: 'Settlement docs scanned across all groups.', source: 'Read from `groups/{groupId}/settlements`', rows: [{ label: 'Subcollection', value: 'groups/{groupId}/settlements' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedSettlements) }] })} />
                <DetailMetric label="Plaid" value={formatCount(report.meta.scannedPlaidAccounts)} source="Read from `collectionGroup('plaidAccounts')`" onClick={() => openMetricDetail({ title: 'Plaid account scan metadata', subtitle: 'Plaid-linked account docs scanned across users.', source: "Read from `collectionGroup('plaidAccounts')`", rows: [{ label: 'Collection group', value: 'plaidAccounts' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedPlaidAccounts) }] })} />
                <DetailMetric label="Payout" value={formatCount(report.meta.scannedPayoutAccounts)} source="Read from `collectionGroup('payoutAccount')`" onClick={() => openMetricDetail({ title: 'Payout account scan metadata', subtitle: 'Payout account docs scanned across users.', source: "Read from `collectionGroup('payoutAccount')`", rows: [{ label: 'Collection group', value: 'payoutAccount' }, { label: 'Documents scanned', value: formatCount(report.meta.scannedPayoutAccounts) }] })} />
              </div>
            </section>
          </>
        ) : null}
      </div>

      {activeDrawer ? (
        <div className="fixed inset-0 z-40 bg-slate-950/30 backdrop-blur-sm" onClick={() => {
          setSelectedUser(null);
          setSelectedGroup(null);
        }}>
          <aside
            className="absolute right-0 top-0 h-full w-full max-w-[720px] overflow-y-auto bg-white shadow-[0_20px_90px_rgba(15,23,42,0.24)] lg:w-[720px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-6 py-5 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                    {selectedUser ? 'User detail' : 'Group detail'}
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-950">
                    {selectedUser ? selectedUser.name : selectedGroup?.name}
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    {selectedUser ? selectedUser.phone : selectedGroup?.id}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedUser(null);
                    setSelectedGroup(null);
                  }}
                  className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-6 px-6 py-6">
              {selectedUser && report ? (
                <UserDetailPanel
                  user={selectedUser}
                  report={report}
                  onOpenMetricDetail={openMetricDetail}
                  onOpenOrganizedSplit={openOrganizedSplitDetail}
                />
              ) : null}
              {selectedGroup ? (
                <GroupDetailPanel
                  group={selectedGroup}
                  onOpenMetricDetail={openMetricDetail}
                  onOpenGroupExpense={openGroupExpenseDetail}
                />
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}

      {selectedMetricDetail ? (
        <MetricDetailOverlay
          detail={selectedMetricDetail}
          onClose={() => setSelectedMetricDetail(null)}
          onOpenOrganizedSplit={openOrganizedSplitDetail}
        />
      ) : null}

      {selectedOrganizedSplitDetail ? (
        <OrganizedSplitDetailOverlay
          detail={selectedOrganizedSplitDetail}
          onClose={() => setSelectedOrganizedSplitDetail(null)}
          onLaunchPayerPreview={(invite) => handleLaunchPayerPreview(selectedOrganizedSplitDetail.sessionId, invite)}
        />
      ) : null}

      {selectedGroupExpenseDetail ? (
        <GroupExpenseDetailOverlay
          detail={selectedGroupExpenseDetail}
          onClose={() => setSelectedGroupExpenseDetail(null)}
        />
      ) : null}

      {showIdentityDetailModal && report?.summary?.identityVerification ? (
        <IdentityVerificationDetailModal
          users={report.summary.identityVerification.nonVerifiedUsers || []}
          statusFilter={identityStatusFilter}
          onStatusFilterChange={setIdentityStatusFilter}
          searchQuery={identitySearchQuery}
          onSearchQueryChange={setIdentitySearchQuery}
          onSelectUser={(phone) => {
            const user = report.users.find((u) => u.phone === phone);
            if (user) {
              setSelectedGroup(null);
              setSelectedUser(user);
            }
          }}
          onClose={() => setShowIdentityDetailModal(false)}
        />
      ) : null}
    </div>
  );
};

function IdentityVerificationDetailModal({
  users,
  statusFilter,
  onStatusFilterChange,
  searchQuery,
  onSearchQueryChange,
  onSelectUser,
  onClose,
}: {
  users: NonNullable<DashboardReport['summary']['identityVerification']>['nonVerifiedUsers'];
  statusFilter: 'all' | 'document_required' | 'pending' | 'permanently_rejected' | 'not_started';
  onStatusFilterChange: (status: 'all' | 'document_required' | 'pending' | 'permanently_rejected' | 'not_started') => void;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  onSelectUser: (phone: string) => void;
  onClose: () => void;
}) {
  const filteredUsers = users.filter((u) => {
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      return u.name.toLowerCase().includes(q) || u.phone.toLowerCase().includes(q);
    }
    return true;
  });

  const getBadgeStyle = (status: IdentityVerificationStatus) => {
    switch (status) {
      case 'document_required':
        return 'bg-amber-100 text-amber-900 border-amber-200';
      case 'pending':
        return 'bg-blue-100 text-blue-900 border-blue-200';
      case 'permanently_rejected':
        return 'bg-rose-100 text-rose-900 border-rose-200';
      case 'verified':
        return 'bg-emerald-100 text-emerald-900 border-emerald-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                {users.length} Users
              </span>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Identity Verification</p>
            </div>
            <h3 className="mt-1 text-2xl font-black text-slate-950">Non-Verified Users List</h3>
            <p className="mt-1 text-sm text-slate-500">Users who are not currently verified in the identity system.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-1.5 text-xs font-bold text-slate-600">
              <button
                onClick={() => onStatusFilterChange('all')}
                className={`rounded-xl px-3 py-1.5 transition ${statusFilter === 'all' ? 'bg-white text-slate-950 shadow-xs' : 'hover:text-slate-950'}`}
              >
                All ({users.length})
              </button>
              <button
                onClick={() => onStatusFilterChange('document_required')}
                className={`rounded-xl px-3 py-1.5 transition ${statusFilter === 'document_required' ? 'bg-white text-slate-950 shadow-xs' : 'hover:text-slate-950'}`}
              >
                Doc Required ({users.filter((u) => u.status === 'document_required').length})
              </button>
              <button
                onClick={() => onStatusFilterChange('pending')}
                className={`rounded-xl px-3 py-1.5 transition ${statusFilter === 'pending' ? 'bg-white text-slate-950 shadow-xs' : 'hover:text-slate-950'}`}
              >
                Pending ({users.filter((u) => u.status === 'pending').length})
              </button>
              <button
                onClick={() => onStatusFilterChange('permanently_rejected')}
                className={`rounded-xl px-3 py-1.5 transition ${statusFilter === 'permanently_rejected' ? 'bg-white text-slate-950 shadow-xs' : 'hover:text-slate-950'}`}
              >
                Rejected ({users.filter((u) => u.status === 'permanently_rejected').length})
              </button>
              <button
                onClick={() => onStatusFilterChange('not_started')}
                className={`rounded-xl px-3 py-1.5 transition ${statusFilter === 'not_started' ? 'bg-white text-slate-950 shadow-xs' : 'hover:text-slate-950'}`}
              >
                Not Started ({users.filter((u) => u.status === 'not_started').length})
              </button>
            </div>
            <input
              type="text"
              placeholder="Search name or phone…"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              className="w-full sm:w-60 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-400"
            />
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
          <table className="min-w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.16em] text-slate-400">
                <th className="py-2.5 font-semibold">User</th>
                <th className="py-2.5 font-semibold">Platform</th>
                <th className="py-2.5 font-semibold">Identity Status</th>
                <th className="py-2.5 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-sm text-slate-500">
                    No users match the selected filters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.phone} className="border-b border-slate-100 hover:bg-slate-50/80">
                    <td className="py-3">
                      <p className="font-bold text-slate-950">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.phone}</p>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${user.hasApp ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-700'}`}>
                        {user.hasApp ? 'Mobile App' : 'Web'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${getBadgeStyle(user.status)}`}>
                        {user.statusLabel}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <button
                        onClick={() => {
                          onClose();
                          onSelectUser(user.phone);
                        }}
                        className="rounded-xl border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                      >
                        Inspect User
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GroupExpenseDetailOverlay({
  detail,
  onClose,
}: {
  detail: GroupExpenseDetail;
  onClose: () => void;
}) {
  const participants = Array.isArray(detail.participants) ? detail.participants : [];
  const claimedBy = Array.isArray(detail.claimState?.claimedBy) ? detail.claimState.claimedBy : [];
  const outstandingClaimablePhones = Array.isArray(detail.claimState?.outstandingClaimablePhones)
    ? detail.claimState.outstandingClaimablePhones
    : [];
  const receiptItems = Array.isArray(detail.receipt?.items) ? detail.receipt.items : [];

  return (
    <div className="fixed inset-0 z-[65] bg-slate-950/55 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="w-full max-w-5xl rounded-[32px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">Group expense detail</p>
              <h3 className="mt-2 text-2xl font-black text-slate-950">{detail.title}</h3>
              <p className="mt-2 text-sm text-slate-500">Read-only view of the persisted group ledger expense record.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="max-h-[78vh] space-y-6 overflow-y-auto px-6 py-6">
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <DetailMetric label="Expense ID" value={detail.id} source="`groups/{groupId}/expenses/{expenseId}` document ID" />
              <DetailMetric label="Amount" value={formatCurrency(detail.amount)} source="`groups/{groupId}/expenses/{expenseId}` -> `amount`, `currency`" />
              <DetailMetric label="Status" value={labelizeStatus(detail.status)} source="`groups/{groupId}/expenses/{expenseId}` -> `status`" />
              <DetailMetric label="Source" value={formatExpenseSource(detail.source)} source="`groups/{groupId}/expenses/{expenseId}` -> `source`" />
              <DetailMetric label="Payer" value={detail.payerName || detail.payerPhone || '—'} source="`groups/{groupId}/expenses/{expenseId}` -> `payerPhone`" />
              <DetailMetric label="Cycle" value={detail.cycleId || '—'} source="`groups/{groupId}/expenses/{expenseId}` -> `cycleId`" />
              <DetailMetric label="Occurred" value={formatDateTime(detail.occurredAt)} source="`groups/{groupId}/expenses/{expenseId}` -> `occurredAt`" />
              <DetailMetric label="Created" value={formatDateTime(detail.createdAt)} source="`groups/{groupId}/expenses/{expenseId}` -> `createdAt`" />
              <DetailMetric label="Updated" value={formatDateTime(detail.updatedAt)} source="`groups/{groupId}/expenses/{expenseId}` -> `updatedAt`" />
              <DetailMetric label="Created by" value={detail.createdByName || detail.createdByPhone || '—'} source="`groups/{groupId}/expenses/{expenseId}` -> `createdByPhone`" />
              <DetailMetric label="Source session" value={detail.sourceSessionId || '—'} source="`groups/{groupId}/expenses/{expenseId}` -> `sourceSessionId`, `sourceSessionPath`" />
              <DetailMetric label="Participants" value={formatCount(participants.length)} source="`groups/{groupId}/expenses/{expenseId}` -> `participants`, `claimablePhones`" />
            </section>

            {detail.notes ? (
              <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Notes</h4>
                    <p className="mt-2 text-sm text-slate-700">{detail.notes}</p>
                  </div>
                  <SourceNote source="`groups/{groupId}/expenses/{expenseId}` -> `notes`" />
                </div>
              </section>
            ) : null}

            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-950">Participants</p>
                  <p className="mt-1 text-sm text-slate-500">Resolved payer shares and claimable members for this expense.</p>
                </div>
                <SourceNote source="`groups/{groupId}/expenses/{expenseId}` -> `participants`, `claimablePhones`" />
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-3 py-3 font-semibold">Participant</th>
                      <th className="px-3 py-3 font-semibold">Phone</th>
                      <th className="px-3 py-3 font-semibold">Share</th>
                      <th className="px-3 py-3 font-semibold">Claimable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant) => (
                      <tr key={`${participant.phone}-${participant.shareAmount}`} className="border-b border-slate-100">
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{participant.name}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{participant.phone || '—'}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCurrency(participant.shareAmount)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{participant.isClaimable ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {detail.claimState ? (
              <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-950">Claim state</p>
                    <p className="mt-1 text-sm text-slate-500">AI receipt claim progress and outstanding claimable members.</p>
                  </div>
                  <SourceNote source="`groups/{groupId}/expenses/{expenseId}` -> `claimState.mode`, `claimState.claimedBy`, `claimablePhones`" />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <DetailMetric label="Mode" value={labelizeStatus(detail.claimState.mode)} />
                  <DetailMetric label="Claimed by" value={formatCount(claimedBy.length)} />
                  <DetailMetric label="Outstanding" value={formatCount(outstandingClaimablePhones.length)} />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Claimed by</p>
                    <div className="mt-3 space-y-2 text-sm text-slate-700">
                      {claimedBy.length ? claimedBy.map((member) => (
                        <p key={member.phone}>{member.name || member.phone} <span className="text-slate-400">({member.phone})</span></p>
                      )) : <p>None</p>}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Outstanding claimable</p>
                    <div className="mt-3 space-y-2 text-sm text-slate-700">
                      {outstandingClaimablePhones.length ? outstandingClaimablePhones.map((member) => (
                        <p key={member.phone}>{member.name || member.phone} <span className="text-slate-400">({member.phone})</span></p>
                      )) : <p>None</p>}
                    </div>
                  </div>
                </div>
              </section>
            ) : null}

            {detail.receipt ? (
              <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-950">Receipt detail</p>
                    <p className="mt-1 text-sm text-slate-500">Tax, tip, fees, receipt image, and itemized share breakdowns when present.</p>
                  </div>
                  <SourceNote source="`groups/{groupId}/expenses/{expenseId}` -> `receipt.imageUrl`, `receipt.items`, `receipt.tax`, `receipt.tip`, `receipt.fees`" />
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <DetailMetric label="Tax" value={detail.receipt.tax !== null ? formatCurrency(detail.receipt.tax) : '—'} />
                  <DetailMetric label="Tip" value={detail.receipt.tip !== null ? formatCurrency(detail.receipt.tip) : '—'} />
                  <DetailMetric label="Fees" value={detail.receipt.fees !== null ? formatCurrency(detail.receipt.fees) : '—'} />
                  <DetailMetric label="Receipt image" value={detail.receipt.imageUrl ? 'Available' : 'Missing'} />
                </div>
                {detail.receipt.imageUrl ? (
                  <a
                    href={detail.receipt.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex text-sm font-semibold text-sky-700 transition hover:text-sky-800"
                  >
                    Open receipt image
                  </a>
                ) : null}
                <div className="mt-5 space-y-3">
                  {receiptItems.length ? receiptItems.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-bold text-slate-950">{item.name}</p>
                          <p className="mt-1 text-xs text-slate-500">{item.id}</p>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(item.amount)}</span>
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {item.shares.length ? item.shares.map((share) => (
                          <div key={`${item.id}-${share.phone}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                            <p className="font-semibold text-slate-900">{share.name || share.phone}</p>
                            <p className="mt-1 text-xs text-slate-500">{share.phone || '—'} • {(getShareFraction(share) * 100).toFixed(0)}%</p>
                          </div>
                        )) : <p className="text-sm text-slate-500">No share details.</p>}
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                      No receipt items found for this expense.
                    </div>
                  )}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400"
      >
        {children}
      </select>
    </label>
  );
}

function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
      {label}
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold normal-case tracking-normal text-slate-900 outline-none transition focus:border-slate-400"
      />
    </label>
  );
}

function MetricChartCard({
  title,
  value,
  subtitle,
  source,
  chart,
  onClick,
}: {
  title: string;
  value: string;
  subtitle: string;
  source?: string;
  chart: React.ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">{title}</p>
      <p className="mt-3 text-2xl font-black tracking-tight text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      {source ? <SourceNote source={source} className="mt-3" /> : null}
      {onClick ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">View details</p> : null}
      <div className="mt-4">{chart}</div>
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[28px] border border-slate-200 bg-white p-5 text-left shadow-[0_18px_50px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-slate-300"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      {body}
    </div>
  );
}

function UserDetailPanel({
  user,
  report,
  onOpenMetricDetail,
  onOpenOrganizedSplit,
}: {
  user: UserReport;
  report: DashboardReport;
  onOpenMetricDetail: (detail: MetricDetailState) => void;
  onOpenOrganizedSplit: (sessionId: string) => void;
}) {
  const userRecurringParents = report.recurringParentSessionItems.filter((item) => item.organizerPhone === user.phone);
  const userRecurringOptOuts = report.recurringOptOutInviteItems.filter((item) => item.payerPhone === user.phone);
  const userRecurringAutoPay = report.recurringAutoPayInviteItems.filter((item) => item.payerPhone === user.phone);

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <DetailMetric label="Open splits" value={formatCount(user.openSplitsOrganized)} source="`sessions/{sessionId}` -> `organizerPhone`, `createdAt`, `status`, `completedAt`" onClick={() => onOpenMetricDetail({ title: `${user.name}: open organized splits`, subtitle: 'Sessions this user organized in range that are still open.', source: "`sessions/{sessionId}` -> `organizerPhone`, `createdAt`, `status`, `completedAt`", items: user.openSplitsOrganizedItems, itemKind: 'organized_split' })} />
        <DetailMetric label="Settled splits" value={formatCount(user.settledSplitsOrganized)} source="`sessions/{sessionId}` -> `organizerPhone`, `completedAt`, `status`" onClick={() => onOpenMetricDetail({ title: `${user.name}: settled organized splits`, subtitle: 'Sessions this user organized that completed in range.', source: "`sessions/{sessionId}` -> `organizerPhone`, `completedAt`, `status`", items: user.settledSplitsOrganizedItems, itemKind: 'organized_split' })} />
        <DetailMetric label="Payments received" value={formatCurrency(user.paymentsReceivedAmount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> success timestamps + session organizer join" onClick={() => onOpenMetricDetail({ title: `${user.name}: payments received`, subtitle: 'Organizer-side successful payment events for this user.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> success timestamps + session organizer join", rows: buildPaymentFlowRows({ instantCount: user.paymentsReceivedInstantPayoutCount, instantAmount: user.paymentsReceivedInstantPayoutAmount, forceCount: user.paymentsReceivedForceSettlementCount, forceAmount: user.paymentsReceivedForceSettlementAmount }), items: user.paymentsReceived })} />
        <DetailMetric label="Payments done" value={formatCurrency(user.paymentsDoneAmount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `phone`, success timestamps, invite total" onClick={() => onOpenMetricDetail({ title: `${user.name}: payments done`, subtitle: 'Payer-side successful payment events for this user.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `phone`, success timestamps, invite total", rows: buildPaymentFlowRows({ instantCount: user.paymentsDoneInstantPayoutCount, instantAmount: user.paymentsDoneInstantPayoutAmount, forceCount: user.paymentsDoneForceSettlementCount, forceAmount: user.paymentsDoneForceSettlementAmount }), items: user.paymentsDone })} />
        <DetailMetric label="Identity verification" value={user.identityStatusLabel || 'Not Started'} source="`users/{phone}` -> `identityVerification` / `astraUser`" onClick={() => onOpenMetricDetail({ title: `${user.name}: identity verification`, subtitle: 'Identity verification status and history.', source: "`users/{phone}` -> `identityVerification` / `astraUser`", rows: [{ label: 'Status', value: user.identityStatusLabel || 'Not Started' }, { label: 'Internal Code', value: user.identityStatus || 'not_started' }, { label: 'Platform', value: user.hasApp ? 'Mobile App' : 'Web' }] })} />
        <DetailMetric label="Clero+ Subscription" value={user.isCleroPlus ? 'Active' : 'None'} source="`users/{phone}/subscriptions/clero_plus`" onClick={() => onOpenMetricDetail({ title: `${user.name}: Clero+ status`, subtitle: 'Zero-fee subscription status.', source: "`users/{phone}/subscriptions/clero_plus`", rows: [{ label: 'Subscribed', value: user.isCleroPlus ? 'Yes' : 'No' }] })} />
        <DetailMetric label="Gmail Integration" value={user.hasGmailConnected ? 'Connected' : 'Not Connected'} source="`users/{phone}/integrations/gmail`" onClick={() => onOpenMetricDetail({ title: `${user.name}: Gmail connection`, subtitle: 'Connected Gmail receipt parsing state.', source: "`users/{phone}/integrations/gmail`", rows: [{ label: 'Connected', value: user.hasGmailConnected ? 'Yes' : 'No' }] })} />
        <DetailMetric label="Connected cards" value={formatCount(user.cardCount ?? user.connectedCardCount ?? 0)} source="`users/{phone}/plaidAccounts/{accountId}` -> `accountType='credit'`, `bankDisplayName`, `bankLast4`" onClick={() => onOpenMetricDetail({ title: `${user.name}: connected cards`, subtitle: 'Credit card docs linked for this user.', source: "`users/{phone}/plaidAccounts/{accountId}` -> `accountType='credit'`, `bankDisplayName`, `bankLast4`", accounts: user.connectedCards })} />
        <DetailMetric label="Bank accounts" value={formatCount(user.bankCount ?? (user.hasPayoutAccount ? 1 : 0))} source="`users/{phone}/payoutAccount/default` & Plaid depository accounts" onClick={() => onOpenMetricDetail({ title: `${user.name}: bank accounts`, subtitle: 'Astra & Plaid depository bank accounts.', source: "`users/{phone}/payoutAccount/default`", rows: buildPayoutRows(user.payoutAccount) })} />
        <DetailMetric label="Pending Platform" value={formatCurrency(user.pendingPlatform)} source="`users/{phone}` -> `walletBalances.pendingPlatform`" onClick={() => onOpenMetricDetail({ title: `${user.name}: pending platform`, subtitle: 'Current live pending platform balance.', source: "`users/{phone}` -> `walletBalances.pendingPlatform`", rows: [{ label: 'Pending platform', value: formatCurrency(user.pendingPlatform) }] })} />
        <DetailMetric label="ACH successes" value={formatCount(user.riskProfile.achSuccessCount)} source="`users/{phone}` -> `riskProfile.achSuccessCount`" onClick={() => onOpenMetricDetail({ title: `${user.name}: risk profile`, subtitle: 'ACH and instant payout risk profile for this user.', source: "`users/{phone}` -> `riskProfile.*`", rows: buildRiskProfileRows(user) })} />
        <DetailMetric label="ACH failures" value={formatCount(user.riskProfile.achFailureCount)} source="`users/{phone}` -> `riskProfile.achFailureCount`, `riskProfile.achFailCount`" onClick={() => onOpenMetricDetail({ title: `${user.name}: ACH failure history`, subtitle: 'Failure-related fields captured on the user risk profile.', source: "`users/{phone}` -> `riskProfile.achFailureCount`, `riskProfile.achFailCount`, `riskProfile.failedPaymentCount`", rows: buildRiskProfileRows(user) })} />
        <DetailMetric label="Avg ACH payment" value={formatCurrency(user.riskProfile.averagePayment)} source="`users/{phone}` -> `riskProfile.avgPaymentCents`" onClick={() => onOpenMetricDetail({ title: `${user.name}: average ACH payment`, subtitle: 'Lifetime average ACH payment captured on the risk profile.', source: "`users/{phone}` -> `riskProfile.avgPaymentCents`", rows: buildRiskProfileRows(user) })} />
        <DetailMetric label="Instant payout status" value={user.riskProfile.manualReviewRequired ? 'Manual review' : (user.riskProfile.instantPayoutDisabledReason ? 'Disabled' : 'Eligible')} source="`users/{phone}` -> `riskProfile.manualReviewRequired`, `riskProfile.instantPayoutDisabledReason`, `riskProfile.instantDisabledUntil`" onClick={() => onOpenMetricDetail({ title: `${user.name}: instant payout risk state`, subtitle: 'Current instant payout gating fields from the user risk profile.', source: "`users/{phone}` -> `riskProfile.manualReviewRequired`, `riskProfile.instantPayoutDisabledReason`, `riskProfile.instantDisabledUntil`", rows: buildRiskProfileRows(user) })} />
        <DetailMetric label="Recurring parents" value={formatCount(user.recurringParentSplitCount)} source="`sessions/{sessionId}` -> `isRecurring`, `organizerPhone`" onClick={() => onOpenMetricDetail({ title: `${user.name}: recurring parents`, subtitle: 'Recurring parent sessions organized by this user.', source: "`sessions/{sessionId}` -> `isRecurring`, `organizerPhone`", items: userRecurringParents, itemKind: 'organized_split' })} />
        <DetailMetric label="Auto-pay on" value={formatCount(user.recurringAutoPayEnabledCount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`, `phone`" onClick={() => onOpenMetricDetail({ title: `${user.name}: recurring auto-pay`, subtitle: 'Recurring invite rows where this user has auto-pay enabled.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringAutoPayEnabled`, `phone`", items: userRecurringAutoPay })} />
        <DetailMetric label="Opted out" value={formatCount(user.recurringOptedOutCount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringOptOut`, `status='declined_recurring'`" onClick={() => onOpenMetricDetail({ title: `${user.name}: recurring opt-outs`, subtitle: 'Recurring invite rows where this user opted out.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` -> `recurringOptOut`, `status='declined_recurring'`", items: userRecurringOptOuts })} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <MiniTrendCard title="Organized vs settled" values={user.series.map((point) => point.organizedSplits + point.settledSplits)} accent="#0f766e" source="Daily buckets from `sessions/{sessionId}` -> `createdAt`, `completedAt`, `status`, filtered by organizer phone" onClick={() => onOpenMetricDetail({ title: `${user.name}: organized vs settled by day`, subtitle: 'Daily bucket totals for organized and settled split activity.', source: "`sessions/{sessionId}` -> `createdAt`, `completedAt`, `status`, filtered by organizer phone", rows: buildSeriesRows(user.series as unknown as Array<{ date: string } & Record<string, number>>, (point) => point.organizedSplits + point.settledSplits) })} />
        <MiniTrendCard title="Payments received vs done" values={user.series.map((point) => point.paymentsReceived + point.paymentsDone)} accent="#2563eb" source="Daily buckets from canonical `sessions/{sessionId}/invites/{inviteId}` success timestamps, split by organizer and payer phone" onClick={() => onOpenMetricDetail({ title: `${user.name}: payments by day`, subtitle: 'Daily bucket totals for payment activity.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` success timestamps, split by organizer and payer phone", rows: buildSeriesRows(user.series as unknown as Array<{ date: string } & Record<string, number>>, (point) => point.paymentsReceived + point.paymentsDone) })} />
      </div>

      <ListSection title="Organized splits" subtitle="Click any row to view the full split details." items={user.organizedSplits} source="`sessions/{sessionId}` filtered by `organizerPhone`" itemKind="organized_split" onOpenOrganizedSplit={onOpenOrganizedSplit} />
      <ListSection title="Paid splits" subtitle="Successful payer-side events tied to sessions." items={user.paidSplits} source="Canonical `sessions/{sessionId}/invites/{inviteId}` filtered by `phone` + `sessions/{sessionId}` join" />
      <ListSection title="Payments received" subtitle="Organizer-side payment events for this user." items={user.paymentsReceived} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events + `sessions/{sessionId}.organizerPhone`" />
      <ListSection title="Payments done" subtitle="Payer-side payment events for this user." items={user.paymentsDone} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events + `phone`" />
    </>
  );
}

function GroupDetailPanel({
  group,
  onOpenMetricDetail,
  onOpenGroupExpense,
}: {
  group: GroupReport;
  onOpenMetricDetail: (detail: MetricDetailState) => void;
  onOpenGroupExpense: (expenseId: string) => void;
}) {
  const members = Array.isArray(group.members) ? group.members : [];
  const expenses = Array.isArray(group.expenses) ? group.expenses : [];
  const memberRows = members.map((member) => ({
    label: member.name || member.phone,
    value: `${labelizeGroupMemberStatus(member.status)} • ${member.phone}`,
  }));
  const activeMemberRows = members
    .filter((member) => member.status === 'active')
    .map((member) => ({
      label: member.name || member.phone,
      value: member.phone,
    }));
  const pendingMemberRows = members
    .filter((member) => member.status === 'pending')
    .map((member) => ({
      label: member.name || member.phone,
      value: member.phone,
    }));
  const adminRows = group.admin ? [
    { label: 'Admin', value: group.admin.name || group.admin.phone },
    { label: 'Phone', value: group.admin.phone },
    { label: 'Status', value: labelizeGroupMemberStatus(group.admin.status) },
    { label: 'Added', value: formatDateTime(group.admin.addedAt) },
    { label: 'Joined', value: formatDateTime(group.admin.joinedAt) },
  ] : [];
  const groupedExpenses = expenses.reduce<Record<string, GroupExpenseItem[]>>((accumulator, expense) => {
    const key = expense.createdAt || expense.occurredAt || 'Unknown date';
    const bucketLabel = key === 'Unknown date' ? key : formatDate(key, key);
    if (!accumulator[bucketLabel]) accumulator[bucketLabel] = [];
    accumulator[bucketLabel].push(expense);
    return accumulator;
  }, {});

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <DetailMetric label="Group type" value={labelizeStatus(group.groupType)} source="`groups/{groupId}` -> `settlementType`" onClick={() => onOpenMetricDetail({ title: `${group.name}: group type`, subtitle: 'Settlement configuration for this group.', source: "`groups/{groupId}` -> `settlementType`", rows: [{ label: 'Group type', value: labelizeStatus(group.groupType) }] })} />
        <DetailMetric label="Next settlement" value={group.nextSettlementLabel} source="`groups/{groupId}` -> `settlementConfig.monthlyDay`, `settlementConfig.settleAtDate`, `settlementConfig.timezone`" onClick={() => onOpenMetricDetail({ title: `${group.name}: next settlement`, subtitle: 'Computed next settlement schedule for this group.', source: "`groups/{groupId}` -> `settlementConfig.monthlyDay`, `settlementConfig.settleAtDate`, `settlementConfig.timezone`", rows: [{ label: 'Next settlement', value: group.nextSettlementLabel }, { label: 'ISO datetime', value: group.nextSettlementAt || '—' }] })} />
        <DetailMetric label="Current expenses" value={formatCurrency(group.currentExpensesTotal)} source="`groups/{groupId}/balances/{activeCycleId}` -> `totalVolume`" onClick={() => onOpenMetricDetail({ title: `${group.name}: current expenses`, subtitle: 'Current active-cycle expense total for this group.', source: "`groups/{groupId}/balances/{activeCycleId}` -> `totalVolume`", rows: [{ label: 'Current expenses total', value: formatCurrency(group.currentExpensesTotal) }] })} />
        <DetailMetric label="Expense history" value={formatCount(group.expenseCount)} source="`groups/{groupId}/expenses/{expenseId}` -> `title`, `source`, `amount`, `status`, `createdAt`, `occurredAt`" onClick={() => onOpenMetricDetail({ title: `${group.name}: expense history`, subtitle: 'All group expenses across cycles, including voided entries.', source: "`groups/{groupId}/expenses/{expenseId}` -> `title`, `source`, `amount`, `status`, `createdAt`, `occurredAt`", rows: [{ label: 'Expense rows', value: formatCount(group.expenseCount) }, { label: 'Non-voided expense volume', value: formatCurrency(group.expenseVolume) }] })} />
        <DetailMetric label="Settlements created" value={formatCount(group.settlementCount)} source="`groups/{groupId}/settlements/{settlementId}`" onClick={() => onOpenMetricDetail({ title: `${group.name}: settlement docs`, subtitle: 'All settlement docs found for this group.', source: "`groups/{groupId}/settlements/{settlementId}`", settlements: group.settlementDocs })} />
        <DetailMetric label="Admin" value={group.admin?.name || group.admin?.phone || '—'} source="`groups/{groupId}` -> `adminPhone`, `membershipByPhone`" onClick={() => onOpenMetricDetail({ title: `${group.name}: admin`, subtitle: 'Current group admin and membership metadata.', source: "`groups/{groupId}` -> `adminPhone`, `membershipByPhone`", rows: adminRows })} />
        <DetailMetric label="Members" value={formatCount(members.length)} source="`groups/{groupId}` -> `membershipByPhone`, `activeMemberPhones`" onClick={() => onOpenMetricDetail({ title: `${group.name}: members`, subtitle: 'All group members and statuses.', source: "`groups/{groupId}` -> `membershipByPhone`, `activeMemberPhones`", rows: memberRows })} />
        <DetailMetric label="Active members" value={formatCount(group.activeMemberCount)} source="`groups/{groupId}` -> `membershipByPhone.status='active'`, `activeMemberPhones`" onClick={() => onOpenMetricDetail({ title: `${group.name}: active members`, subtitle: 'Members currently active in this group.', source: "`groups/{groupId}` -> `membershipByPhone.status='active'`, `activeMemberPhones`", rows: activeMemberRows })} />
        <DetailMetric label="Open settlement splits" value={formatCount(group.openSettlementSplits)} source="`sessions/{sessionId}` -> `splitSource='group_settlement'`, `settlementMeta.groupId`, `status`" onClick={() => onOpenMetricDetail({ title: `${group.name}: open settlement splits`, subtitle: 'Group settlement splits created in range and still open.', source: "`sessions/{sessionId}` -> `splitSource='group_settlement'`, `settlementMeta.groupId`, `status`", items: group.openSettlementSplitsItems })} />
        <DetailMetric label="Settled settlement splits" value={formatCount(group.settledSettlementSplits)} source="`sessions/{sessionId}` -> `splitSource='group_settlement'`, `settlementMeta.groupId`, `completedAt`" onClick={() => onOpenMetricDetail({ title: `${group.name}: settled settlement splits`, subtitle: 'Group settlement splits completed in range.', source: "`sessions/{sessionId}` -> `splitSource='group_settlement'`, `settlementMeta.groupId`, `completedAt`", items: group.settledSettlementSplitsItems })} />
        <DetailMetric label="Payments received" value={formatCurrency(group.paymentsReceivedAmount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions" onClick={() => onOpenMetricDetail({ title: `${group.name}: payments received`, subtitle: 'Successful payment events attributed to this group.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions", rows: buildPaymentFlowRows({ instantCount: group.paymentsReceivedInstantPayoutCount, instantAmount: group.paymentsReceivedInstantPayoutAmount, forceCount: group.paymentsReceivedForceSettlementCount, forceAmount: group.paymentsReceivedForceSettlementAmount }), items: group.paymentsReceived })} />
        <DetailMetric label="Payments done" value={formatCurrency(group.paymentsDoneAmount)} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions" onClick={() => onOpenMetricDetail({ title: `${group.name}: payments done`, subtitle: 'Successful payer events attributed to this group.', source: "Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions", rows: buildPaymentFlowRows({ instantCount: group.paymentsDoneInstantPayoutCount, instantAmount: group.paymentsDoneInstantPayoutAmount, forceCount: group.paymentsDoneForceSettlementCount, forceAmount: group.paymentsDoneForceSettlementAmount }), items: group.paymentsDone })} />
        <DetailMetric label="Created" value={formatDate(group.createdAt)} source="`groups/{groupId}` -> `createdAt`" onClick={() => onOpenMetricDetail({ title: `${group.name}: created`, subtitle: 'Creation timestamp for this group.', source: "`groups/{groupId}` -> `createdAt`", rows: [{ label: 'Created', value: formatDateTime(group.createdAt) }] })} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <MiniTrendCard title="Settlement split activity" values={group.series.map((point) => point.settlementSplitsCreated + point.settlementSplitsCompleted)} accent="#ea580c" source="Daily buckets from group settlement `sessions/{sessionId}` -> `createdAt`, `completedAt`, `settlementMeta.groupId`" onClick={() => onOpenMetricDetail({ title: `${group.name}: settlement split activity`, subtitle: 'Daily bucket totals for group settlement split activity.', source: "group settlement `sessions/{sessionId}` -> `createdAt`, `completedAt`, `settlementMeta.groupId`", rows: buildSeriesRows(group.series as unknown as Array<{ date: string } & Record<string, number>>, (point) => point.settlementSplitsCreated + point.settlementSplitsCompleted) })} />
        <MiniTrendCard title="Group payment activity" values={group.series.map((point) => point.paymentsReceived + point.paymentsDone)} accent="#164e63" source="Daily buckets from successful canonical `sessions/{sessionId}/invites/{inviteId}` events joined to group settlement sessions" onClick={() => onOpenMetricDetail({ title: `${group.name}: group payment activity`, subtitle: 'Daily bucket totals for payment activity attributed to this group.', source: "Successful canonical `sessions/{sessionId}/invites/{inviteId}` events joined to group settlement sessions", rows: buildSeriesRows(group.series as unknown as Array<{ date: string } & Record<string, number>>, (point) => point.paymentsReceived + point.paymentsDone) })} />
      </div>

      <ListSection title="Group-linked splits" subtitle="Settlement sessions associated with this group." items={group.splits} source="`sessions/{sessionId}` -> `splitSource='group_settlement'`, `settlementMeta.groupId`" />
      <ListSection title="Group payments received" subtitle="Successful payment events attributed to this group." items={group.paymentsReceived} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions by `sessionId`" />
      <ListSection title="Group payments done" subtitle="Successful payer events attributed to this group." items={group.paymentsDone} source="Canonical `sessions/{sessionId}/invites/{inviteId}` success events joined to group settlement sessions by `sessionId`" />

      <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-950">Expenses</h3>
            <p className="mt-1 text-sm text-slate-500">Full group expense history across cycles, including voided entries.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
              {formatCount(group.expenseCount)} expenses
            </span>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">
              {formatCurrency(group.expenseVolume)} non-voided volume
            </span>
          </div>
        </div>
        <SourceNote source="`groups/{groupId}/expenses/{expenseId}` -> `title`, `source`, `amount`, `currency`, `payerPhone`, `cycleId`, `status`, `participants`, `receipt`, `claimState`, `createdAt`, `occurredAt`, `updatedAt`, `sourceSessionId`, `sourceSessionPath`" className="mt-4" />

        <div className="mt-5 space-y-5">
          {expenses.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
              No expenses found for this group.
            </div>
          ) : (
            Object.entries(groupedExpenses).map(([dateLabel, expenseRows]) => (
              <div key={dateLabel}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{dateLabel}</p>
                <div className="mt-3 space-y-3">
                  {expenseRows.map((expense) => (
                    <button
                      key={expense.id}
                      type="button"
                      onClick={() => onOpenGroupExpense(expense.id)}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-left transition hover:border-slate-300 hover:bg-slate-100/80"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="truncate text-sm font-bold text-slate-950">{expense.title}</p>
                            <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${
                              expense.status === 'voided' ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                            }`}>
                              {labelizeStatus(expense.status)}
                            </span>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                              {formatExpenseSource(expense.source)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-slate-500">
                            Paid by {expense.payerName || expense.payerPhone || 'Unknown payer'}
                          </p>
                          <p className="mt-2 text-xs text-slate-400">
                            {expense.participantCount} participants
                            {expense.cycleId ? ` • Cycle ${expense.cycleId}` : ''}
                            {expense.occurredAt ? ` • Occurred ${formatDateTime(expense.occurredAt)}` : expense.createdAt ? ` • Created ${formatDateTime(expense.createdAt)}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-slate-900">{formatCurrency(expense.amount)}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="rounded-[28px] border border-slate-200 bg-slate-50/60 p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-950">Admin & members</h3>
            <p className="mt-1 text-sm text-slate-500">Membership roster pulled from the group doc, including invite status.</p>
          </div>
          <SourceNote source="`groups/{groupId}` -> `adminPhone`, `membershipByPhone`, `activeMemberPhones`, `pendingMemberPhones`" />
        </div>

        <div className="mt-4 space-y-3">
          {members.map((member) => (
            <div key={member.phone} className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-950">{member.name || member.phone}</p>
                  <p className="mt-1 text-sm text-slate-500">{member.phone}</p>
                  <p className="mt-2 text-xs text-slate-400">
                    Added {formatDateTime(member.addedAt)}{member.joinedAt ? ` • Joined ${formatDateTime(member.joinedAt)}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {member.isAdmin ? (
                    <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                      Admin
                    </span>
                  ) : null}
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${
                    member.status === 'active'
                      ? 'bg-emerald-50 text-emerald-700'
                      : member.status === 'pending'
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                  }`}>
                    {labelizeGroupMemberStatus(member.status)}
                  </span>
                  {member.hasCleroAccount === false ? (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                      Off app
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function MiniTrendCard({
  title,
  values,
  accent,
  source,
  onClick,
}: {
  title: string;
  values: number[];
  accent: string;
  source?: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      <p className="text-sm font-bold text-slate-950">{title}</p>
      {source ? <SourceNote source={source} className="mt-3" /> : null}
      {onClick ? <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">View details</p> : null}
      <div className="mt-4">
        <Sparkline values={values} stroke={accent} fill={`${accent}1A`} />
      </div>
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[28px] border border-slate-200 bg-white p-5 text-left shadow-[0_18px_50px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:border-slate-300"
    >
      {body}
    </button>
  ) : (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      {body}
    </div>
  );
}

function MetricDetailOverlay({
  detail,
  onClose,
  onOpenOrganizedSplit,
}: {
  detail: MetricDetailState;
  onClose: () => void;
  onOpenOrganizedSplit: (sessionId: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/45 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="w-full max-w-4xl rounded-[32px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">Metric details</p>
              <h3 className="mt-2 text-2xl font-black text-slate-950">{detail.title}</h3>
              {detail.subtitle ? <p className="mt-2 text-sm text-slate-500">{detail.subtitle}</p> : null}
              {detail.source ? <SourceNote source={detail.source} className="mt-3" /> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              Close
            </button>
          </div>

          <div className="max-h-[70vh] space-y-6 overflow-y-auto px-6 py-6">
            {detail.rows?.length ? (
              <section>
                <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Rows</h4>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {detail.rows.map((row) => (
                    <div key={`${row.label}-${row.value}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{row.label}</p>
                      <p className="mt-2 text-sm font-semibold text-slate-900 break-all">{row.value}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {detail.accounts?.length ? (
              <section>
                <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Accounts</h4>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {detail.accounts.map((account) => (
                    <div key={account.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                      <p className="text-sm font-bold text-slate-950">{account.label}</p>
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        <p><span className="font-semibold text-slate-800">Type:</span> {formatAccountType(account.accountType)}</p>
                        <p><span className="font-semibold text-slate-800">Last 4:</span> {account.last4 ? `•••• ${account.last4}` : '—'}</p>
                        <p><span className="font-semibold text-slate-800">Status:</span> {account.status ? labelizeStatus(account.status) : '—'}</p>
                        <p><span className="font-semibold text-slate-800">Payment method:</span> {account.paymentMethodId || '—'}</p>
                        <p><span className="font-semibold text-slate-800">Plaid item:</span> {account.plaidItemId || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {detail.settlements?.length ? (
              <section>
                <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Settlement docs</h4>
                <div className="mt-4 space-y-3">
                  {detail.settlements.map((settlement) => (
                    <div key={settlement.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-bold text-slate-950">{settlement.groupName}</p>
                          <p className="mt-1 text-xs text-slate-500">{settlement.groupId}</p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {labelizeStatus(settlement.status)}
                        </span>
                      </div>
                      <div className="mt-3 text-sm text-slate-600">
                        <p><span className="font-semibold text-slate-800">Settlement ID:</span> {settlement.id}</p>
                        <p><span className="font-semibold text-slate-800">Event:</span> {formatDateTime(settlement.eventAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {detail.items?.length ? (
              <ListSection
                title="Underlying items"
                subtitle="Rows that contributed to this metric."
                items={detail.items}
                itemKind={detail.itemKind}
                onOpenOrganizedSplit={onOpenOrganizedSplit}
              />
            ) : null}

            {!detail.rows?.length && !detail.accounts?.length && !detail.settlements?.length && !detail.items?.length ? (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-500">
                No detail rows available for this metric.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrganizedSplitDetailOverlay({
  detail,
  onClose,
  onLaunchPayerPreview,
}: {
  detail: OrganizedSplitDetail;
  onClose: () => void;
  onLaunchPayerPreview: (invite: OrganizedSplitInviteDetail) => void;
}) {
  const hasSchedule = Boolean(detail.checkin || detail.checkout || detail.guests !== null);
  const hasItems = detail.items.length > 0;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/55 backdrop-blur-sm" onClick={onClose}>
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="w-full max-w-6xl rounded-[32px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)]"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">Organized split detail</p>
              <h3 className="mt-2 text-2xl font-black text-slate-950">{detail.title}</h3>
              <p className="mt-2 text-sm text-slate-500">
                Organizer view mirrored from persisted session and invite data.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>

          <div className="max-h-[78vh] space-y-6 overflow-y-auto px-6 py-6">
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <DetailMetric label="Session ID" value={detail.sessionId} source="`sessions/{sessionId}` document ID" />
              <DetailMetric label="Status" value={labelizeStatus(detail.status)} source="`sessions/{sessionId}` -> `status`" />
              <DetailMetric label="Amount" value={formatCurrency(detail.amount)} source="`sessions/{sessionId}` -> `amount`, `currency`" />
              <DetailMetric label="Per person" value={detail.perPerson !== null ? formatCurrency(detail.perPerson) : '—'} source="`sessions/{sessionId}` -> `perPerson`" />
              <DetailMetric label="Organizer" value={detail.organizerName || detail.organizerPhone} source="`sessions/{sessionId}` -> `organizerName`, `organizerPhone`" />
              <DetailMetric label="People count" value={detail.peopleCount !== null ? formatCount(detail.peopleCount) : '—'} source="`sessions/{sessionId}` -> `peopleCount`" />
              <DetailMetric label="Created" value={formatDateTime(detail.createdAt)} source="`sessions/{sessionId}` -> `createdAt`" />
              <DetailMetric label="Completed" value={formatDateTime(detail.completedAt)} source="`sessions/{sessionId}` -> `completedAt`" />
            </section>

            <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <p className="text-sm font-bold text-slate-950">Payment progress</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <DetailMetric label="Invites" value={formatCount(detail.collectionSummary.totalInvites)} source="`sessions/{sessionId}/invites/{inviteId}` count" />
                  <DetailMetric label="Paid / settled" value={formatCount(detail.collectionSummary.paidOrSettledInvites)} source="`sessions/{sessionId}/invites/{inviteId}` -> settled statuses" />
                  <DetailMetric label="Collected" value={formatCurrency(detail.collectionSummary.collectedAmount)} source="Successful invite totals from `sessions/{sessionId}/invites/{inviteId}`" />
                  <DetailMetric label="Outstanding" value={formatCurrency(detail.collectionSummary.outstandingAmount)} source="Outstanding invite totals from `sessions/{sessionId}/invites/{inviteId}`" />
                </div>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
                <p className="text-sm font-bold text-slate-950">Split metadata</p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <DetailMetric label="Source" value={detail.site || '—'} source="`sessions/{sessionId}` -> `site`" />
                  <DetailMetric label="Receipt split" value={formatBooleanStatus(detail.isReceipt)} source="`sessions/{sessionId}` -> `isReceipt`" />
                  <DetailMetric label="Item claiming" value={formatBooleanStatus(detail.isItemClaimingEnabled)} source="`sessions/{sessionId}` -> `isItemClaimingEnabled`" />
                  <DetailMetric label="Tax / tip" value={`${detail.tax !== null ? formatCurrency(detail.tax) : '—'} / ${detail.tipAmount !== null ? formatCurrency(detail.tipAmount) : '—'}`} source="`sessions/{sessionId}` -> `tax`, `tipAmount`, `tip`" />
                </div>
                {hasSchedule ? (
                  <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-600">
                    <p><span className="font-semibold text-slate-800">Check-in:</span> {detail.checkin || '—'}</p>
                    <p><span className="font-semibold text-slate-800">Check-out:</span> {detail.checkout || '—'}</p>
                    <p><span className="font-semibold text-slate-800">Guests:</span> {detail.guests !== null ? formatCount(detail.guests) : '—'}</p>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-950">Participants</p>
                  <p className="mt-1 text-sm text-slate-500">Organizer plus invite participants derived from the split.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {formatCount(detail.participants.length)}
                </span>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-3 py-3 font-semibold">Participant</th>
                      <th className="px-3 py-3 font-semibold">Role</th>
                      <th className="px-3 py-3 font-semibold">Amount</th>
                      <th className="px-3 py-3 font-semibold">Items</th>
                      <th className="px-3 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.participants.map((participant) => (
                      <tr key={`${participant.role}-${participant.phone || participant.name}`} className="border-b border-slate-100">
                        <td className="px-3 py-4">
                          <p className="font-bold text-slate-950">{participant.name}</p>
                          <p className="text-xs text-slate-500">{participant.phone || '—'}</p>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-600">{labelizeStatus(participant.role)}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-slate-900">{formatCurrency(participant.amount)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{formatCount(participant.itemCount)}</td>
                        <td className="px-3 py-4 text-sm text-slate-600">{labelizeStatus(participant.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-950">Invite roster</p>
                  <p className="mt-1 text-sm text-slate-500">Current status and payment progress for every invite in this split.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {formatCount(detail.invites.length)}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {detail.invites.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                    No invite rows were found for this split.
                  </div>
                ) : detail.invites.map((invite) => (
                  <div key={invite.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-bold text-slate-950">{invite.name}</p>
                        <p className="mt-1 text-xs text-slate-500">{invite.phone || '—'}</p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => onLaunchPayerPreview(invite)}
                          disabled={!invite.inviteToken}
                          className="rounded-full border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-amber-800 transition hover:border-amber-300 hover:bg-amber-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                        >
                          {invite.inviteToken ? 'Preview payer view' : 'No token'}
                        </button>
                        {invite.isPartialPayment || invite.status === 'partially_paid' ? (
                          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-800">
                            Partially Paid ({formatCurrency(invite.paidAmount ?? 0)} / {formatCurrency(invite.amount)})
                          </span>
                        ) : (
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            {labelizeStatus(invite.status)}
                          </span>
                        )}
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(invite.amount)}</span>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                      <p><span className="font-semibold text-slate-800">Method:</span> {invite.paymentMethod ? labelizeStatus(invite.paymentMethod) : '—'}</p>
                      <p><span className="font-semibold text-slate-800">Paid event:</span> {formatDateTime(invite.eventAt)}</p>
                      {invite.isPartialPayment || invite.status === 'partially_paid' ? (
                        <p><span className="font-semibold text-slate-800">Remaining:</span> {formatCurrency(invite.remainingAmount ?? 0)}</p>
                      ) : (
                        <p><span className="font-semibold text-slate-800">Claimed items:</span> {formatCount(invite.claimedItemCount)}</p>
                      )}
                      <p><span className="font-semibold text-slate-800">Claimed amount:</span> {formatCurrency(invite.claimedAmount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-slate-950">Split composition</p>
                  <p className="mt-1 text-sm text-slate-500">Receipt items and their assigned shares when this split was itemized.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700">
                  {formatCount(detail.items.length)}
                </span>
              </div>
              {hasItems ? (
                <div className="mt-4 space-y-3">
                  {detail.items.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-sm font-bold text-slate-950">{item.name}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {item.isTip ? 'Tip line' : 'Receipt item'} • {formatCount(item.shareCount)} shares
                          </p>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(item.amount)}</span>
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {item.shares.map((share) => (
                          <div key={`${item.id}-${share.phone}-${share.name}`} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-3 text-sm text-slate-600">
                            <p className="font-semibold text-slate-900">{share.name}</p>
                            <p className="mt-1 text-xs text-slate-500">{share.phone || '—'}</p>
                            <p className="mt-2"><span className="font-semibold text-slate-800">Fraction:</span> {(getShareFraction(share) * 100).toFixed(0)}%</p>
                            <p><span className="font-semibold text-slate-800">Amount:</span> {formatCurrency(share.amount)}</p>
                            <p><span className="font-semibold text-slate-800">Status:</span> {labelizeStatus(share.status)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
                  This split does not have persisted receipt items. Use the invite roster and participant summary above for the non-itemized breakdown.
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPage;
